import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import GTMService from '../lib/gtm';
import {
  Sparkles,
  Upload,
  FileImage,
  FileSearch,
  Zap,
  Download,
  RotateCcw,
  Eye,
  CheckCircle,
  XCircle,
  Plus,
  X,
  Edit2,
  ArrowLeft,
  History,
  Target,
  Info,
  Search,
  Copy,
  MoreVertical,
  Calendar,
  ChevronUp,
  ChevronDown,
  Image,
  X as XIcon
} from 'lucide-react';
// Simple notification state management instead of react-hot-toast
import { AdGenerationService, AdBrief, AdGenerationResult, AdGeneration } from '../lib/ad-generation';
import { AdAssetsService, AdAsset, ColorPalette, AssetLibrary } from '../lib/ad-assets';
import { CreditService } from '../lib/credits';
import { SecureImage } from '../components/SecureImage';

// Improvement options for regeneration
const improvementOptions = [
  {
    id: 'colors',
    label: 'Colors & Palette',
    description: 'Adjust vibrancy, contrast, or color scheme'
  },
  {
    id: 'layout',
    label: 'Layout & Composition',
    description: 'Reposition elements, adjust spacing, or balance'
  },
  {
    id: 'text',
    label: 'Text & Typography',
    description: 'Improve readability, font styles, or copy'
  },
  {
    id: 'style',
    label: 'Style & Mood',
    description: 'Change overall tone, aesthetics, or visual style'
  },
  {
    id: 'branding',
    label: 'Branding & Logo',
    description: 'Adjust logo placement or brand consistency'
  }
];

// Funny rotating messages for loading state
const loadingMessages = [
  "Teaching AI about marketing magic...",
  "Consulting with creative geniuses...",
  "Mixing pixels with purpose...",
  "Crafting your masterpiece...",
  "Adding a sprinkle of viral potential...",
  "Optimizing for maximum engagement...",
  "Making competitors jealous...",
  "Turning ideas into eye candy...",
  "Cooking up something special...",
  "Applying secret sauce recipe...",
  "Channeling creative energy...",
  "Building your next viral hit...",
  "Polishing pixels to perfection...",
  "Generating awesomeness...",
  "Making marketing dreams come true..."
];

export function AdGeneratorPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Notification state
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
    show: boolean;
  }>({ message: '', type: 'info', show: false });

  // Show notification helper
  const showNotification = useCallback((message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setNotification({ message, type, show: true });
    setTimeout(() => setNotification(prev => ({ ...prev, show: false })), 5000);
  }, []);

  // Form state
  const [brief, setBrief] = useState<Partial<AdBrief>>({
    context: '',
    headline: '',
    cta: '',
    stickerText: '',
    style: 'Modern',
    colors: [], // Empty array means AI will choose colors
    platform: 'Facebook',
    size: 'auto'
  });


  // Asset state
  const [selectedLogo, setSelectedLogo] = useState<AdAsset | null>(null);
  const [selectedReferenceImages, setSelectedReferenceImages] = useState<AdAsset[]>([]);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingReferences, setIsUploadingReferences] = useState(false);

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationResult, setGenerationResult] = useState<AdGenerationResult | null>(null);
  const [generationHistory, setGenerationHistory] = useState<AdGeneration[]>([]);
  

  // UI state
  const [activeStep, setActiveStep] = useState<'brief' | 'assets' | 'generate' | 'result'>('brief');
  const [dragOver, setDragOver] = useState<'logo' | 'reference' | null>(null);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  
  // Asset selection modes for main Assets step
  const [logoSelectionMode, setLogoSelectionMode] = useState<'upload' | 'library'>('upload');
  const [referenceSelectionMode, setReferenceSelectionMode] = useState<'upload' | 'library'>('upload');
  
  // Library selection states for Assets step
  const [showLogoLibraryInAssets, setShowLogoLibraryInAssets] = useState(false);
  const [showReferenceLibraryInAssets, setShowReferenceLibraryInAssets] = useState(false);
  const [selectedLibraryLogoInAssets, setSelectedLibraryLogoInAssets] = useState<AdAsset | null>(null);
  const [selectedLibraryReferencesInAssets, setSelectedLibraryReferencesInAssets] = useState<AdAsset[]>([]);
  
  // Regeneration modal state
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [regenerationFeedback, setRegenerationFeedback] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerationError, setRegenerationError] = useState<string | null>(null);
  
  // History section state
  const [historySearchTerm, setHistorySearchTerm] = useState('');
  const [showMoreHistory, setShowMoreHistory] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(true);
  
  // History pagination state
  const [historyLimit, setHistoryLimit] = useState(6);
  const [isLoadingMoreHistory, setIsLoadingMoreHistory] = useState(false);
  const [totalHistoryCount, setTotalHistoryCount] = useState(0);
  
  // Enhanced filter state
  const [historyFilters, setHistoryFilters] = useState({
    platform: 'all' as string,
    style: 'all' as string,
    size: 'all' as string,
    dateRange: 'all' as string,
    customDateStart: null as Date | null,
    customDateEnd: null as Date | null
  });
  const [recommendedPalettes, setRecommendedPalettes] = useState<ColorPalette[]>([]);
  
  // Asset library state
  const [assetLibrary, setAssetLibrary] = useState<AssetLibrary>({ logos: [], referenceImages: [], totalCount: 0 });
  const [showAssetLibrary, setShowAssetLibrary] = useState(false);
  const [assetSearchTerm, setAssetSearchTerm] = useState('');
  const [selectedAssetType, setSelectedAssetType] = useState<'all' | 'logo' | 'reference'>('all');
  const [showCreativeModal, setShowCreativeModal] = useState(false);
  const [selectedCreative, setSelectedCreative] = useState<AdGeneration | null>(null);
  
  // State to track which generation is being regenerated (current result or from history)
  const [regeneratingGeneration, setRegeneratingGeneration] = useState<AdGeneration | AdGenerationResult | null>(null);

  // Credit info
  const [creditBalance, setCreditBalance] = useState<number>(0);


  // Body scroll lock utilities
  const lockBodyScroll = useCallback(() => {
    const scrollY = document.documentElement.style.getPropertyValue('--scroll-y');
    const body = document.body;
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}`;
    body.style.left = '0';
    body.style.right = '0';
  }, []);

  const unlockBodyScroll = useCallback(() => {
    const body = document.body;
    const html = document.documentElement;
    const scrollY = body.style.top;
    
    // Reset all body and html styles
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.overflow = '';
    body.style.pointerEvents = '';
    html.style.overflow = '';
    
    // Restore scroll position, with fallback to top if position is invalid
    try {
      const scrollPosition = parseInt(scrollY || '0') * -1;
      window.scrollTo(0, isNaN(scrollPosition) ? 0 : scrollPosition);
    } catch {
      // Fallback: scroll to top if anything goes wrong
      window.scrollTo(0, 0);
    }
  }, []);

  // Track scroll position for body lock
  useEffect(() => {
    const updateScrollVar = () => {
      document.documentElement.style.setProperty('--scroll-y', `${window.scrollY}px`);
    };
    document.addEventListener('scroll', updateScrollVar);
    return () => document.removeEventListener('scroll', updateScrollVar);
  }, []);

  // Function to clean up regeneration modal state
  // Data loading functions - defined first to avoid hoisting issues
  const loadAssetLibrary = useCallback(async () => {
    try {
      const library = await AdAssetsService.getAssetLibrary();
      setAssetLibrary(library);
    } catch (error) {
      console.error('Error loading asset library:', error);
    }
  }, []);

  const loadGenerationHistory = useCallback(async (limit: number = historyLimit) => {
    try {
      setIsHistoryLoading(true);
      const history = await AdGenerationService.getGenerationHistory(limit);
      setGenerationHistory(history.generations);
      setTotalHistoryCount(history.totalCount);
    } catch (error) {
      console.error('Error loading generation history:', error);
      // Show user-friendly error instead of leaving in loading state
      showNotification('Unable to load generation history. Please refresh the page.', 'error');
    } finally {
      setIsHistoryLoading(false);
    }
  }, [historyLimit, showNotification]);

  const loadRecommendedPalettes = useCallback(async () => {
    try {
      const palettes = await AdAssetsService.getRecommendedColorPalettes();
      setRecommendedPalettes(palettes);
    } catch (error) {
      console.error('Error loading recommended palettes:', error);
    }
  }, []);

  const loadCreditBalance = useCallback(async () => {
    try {
      const balance = await CreditService.getCreditBalance();
      setCreditBalance(balance);
    } catch (error) {
      console.error('Error loading credit balance:', error);
    }
  }, []);

  const resetRegenerationModal = useCallback(() => {
    setShowRegenerateModal(false);
    setRegeneratingGeneration(null);
    setRegenerationFeedback('');
    setIsRegenerating(false);
    setRegenerationError(null);
    
    // Force immediate cleanup of all modal-related states
    setTimeout(() => {
      setShowRegenerateModal(false);
      setRegeneratingGeneration(null);
      setIsRegenerating(false);
      
      unlockBodyScroll();
      // Force scroll restoration if needed
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.pointerEvents = '';
    }, 50);
    
    // Reload asset library when modal closes to reflect any new uploads
    loadAssetLibrary();
  }, [loadAssetLibrary, unlockBodyScroll]);

  // Load data on mount
  useEffect(() => {
    loadAssetLibrary();
    loadGenerationHistory();
    loadRecommendedPalettes();
    loadCreditBalance();
  }, [loadAssetLibrary, loadGenerationHistory, loadRecommendedPalettes, loadCreditBalance]);

  // Rotate loading messages (only for main generation, not regeneration)
  useEffect(() => {
    if (activeStep === 'generate' && isGenerating) {
      const interval = setInterval(() => {
        setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [activeStep, isGenerating]);

  // Keyboard shortcuts for regeneration modal
  useEffect(() => {
    if (!showRegenerateModal) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isRegenerating) {
        resetRegenerationModal();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showRegenerateModal, isRegenerating, resetRegenerationModal]);

  // Ensure proper scroll restoration when navigating to result view
  useEffect(() => {
    if (activeStep === 'result' && generationResult) {
      // Force close any lingering modal states
      setShowRegenerateModal(false);
      setRegeneratingGeneration(null);
      setIsRegenerating(false);
      
      // Immediate cleanup
      const cleanup = () => {
        // Reset all body and html styles
        const body = document.body;
        const html = document.documentElement;
        
        body.style.position = '';
        body.style.top = '';
        body.style.left = '';
        body.style.right = '';
        body.style.overflow = '';
        body.style.pointerEvents = '';
        body.style.width = '';
        body.style.height = '';
        body.style.transform = '';
        
        html.style.overflow = '';
        html.style.position = '';
        html.style.pointerEvents = '';
        
        // Remove any backdrop filter or other overlay styles
        const allElements = document.querySelectorAll('*');
        allElements.forEach(el => {
          const element = el as HTMLElement;
          if (element.style.pointerEvents === 'none') {
            element.style.pointerEvents = '';
          }
        });
      };
      
      // Run cleanup immediately and with a delay
      cleanup();
      setTimeout(cleanup, 100);
    }
  }, [activeStep, generationResult]);

  // Cleanup scroll lock when component unmounts or navigates away
  useEffect(() => {
    return () => {
      // Always unlock body scroll when leaving the page
      unlockBodyScroll();
    };
  }, [unlockBodyScroll]);

  const handleDeleteAsset = async (assetId: string, assetType: 'logo' | 'reference') => {
    try {
      await AdAssetsService.deleteAsset(assetId);
      showNotification(`${assetType === 'logo' ? 'Logo' : 'Reference image'} deleted successfully!`, 'success');
      
      // Reload asset library to reflect changes
      await loadAssetLibrary();
    } catch (error) {
      console.error('Error deleting asset:', error);
      showNotification(error instanceof Error ? error.message : 'Failed to delete asset', 'error');
    }
  };

  const handleSearchAssets = async (searchTerm: string) => {
    try {
      if (!searchTerm.trim()) {
        await loadAssetLibrary();
        return;
      }
      
      const assetType = selectedAssetType === 'all' ? undefined : selectedAssetType;
      const searchResults = await AdAssetsService.searchAssets(searchTerm, assetType);
      
      // Separate logos and references from search results
      const logos = searchResults.filter(asset => asset.asset_type === 'logo');
      const referenceImages = searchResults.filter(asset => asset.asset_type === 'reference');
      
      setAssetLibrary({
        logos,
        referenceImages,
        totalCount: searchResults.length
      });
    } catch (error) {
      console.error('Error searching assets:', error);
      showNotification('Failed to search assets', 'error');
    }
  };

  const loadMoreHistory = async () => {
    if (isLoadingMoreHistory) return;
    
    setIsLoadingMoreHistory(true);
    try {
      const newLimit = historyLimit + 6;
      const history = await AdGenerationService.getGenerationHistory(newLimit);
      setGenerationHistory(history.generations);
      setHistoryLimit(newLimit);
      setTotalHistoryCount(history.totalCount);
    } catch (error) {
      console.error('Error loading more history:', error);
      showNotification('Failed to load more history', 'error');
    } finally {
      setIsLoadingMoreHistory(false);
    }
  };

  // Form validation
  const validateBrief = (): { valid: boolean; errors: string[] } => {
    return AdGenerationService.validateBrief(brief);
  };

  // Helper function to change step and scroll to top
  const navigateToStep = (step: 'brief' | 'assets' | 'generate' | 'result') => {
    setActiveStep(step);
    
    // Use a more reliable scroll approach
    setTimeout(() => {
      // Scroll to the top of the page with smooth behavior
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100); // Small delay to ensure DOM is ready
  };

  // Enhanced function to completely reset the form and create a new ad
  const createNewAd = () => {
    // Reset all form state
    setBrief({
      context: '',
      headline: '',
      cta: '',
      stickerText: '',
      style: 'Modern',
      colors: [],
      platform: 'Facebook',
      size: 'auto'
    });
    
    // Reset assets
    setSelectedLogo(null);
    setSelectedReferenceImages([]);
    
    // Reset generation state
    setGenerationResult(null);
    setRegeneratingGeneration(null);
    
    // Reset UI state
    setActiveStep('brief');
    setIsGenerating(false);
    setShowRegenerateModal(false);
    
    // Clear any notifications
    setNotification({ show: false, message: '', type: 'info' });
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Show success message
    showNotification('Ready to create a new ad!', 'info');
  };

  // Asset library handlers for Assets step
  const handleLogoLibrarySelectionInAssets = (logo: AdAsset) => {
    setSelectedLibraryLogoInAssets(logo);
    setSelectedLogo(logo);
    setShowLogoLibraryInAssets(false);
  };

  const handleReferenceLibrarySelectionInAssets = (images: AdAsset[]) => {
    setSelectedLibraryReferencesInAssets(images);
    setSelectedReferenceImages(images);
    setShowReferenceLibraryInAssets(false);
  };

  // File upload handlers
  const handleLogoUpload = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    
    const file = files[0];
    setIsUploadingLogo(true);
    
    try {
      const uploadedAsset = await AdAssetsService.uploadLogo(file);
      setSelectedLogo(uploadedAsset);
      showNotification('Logo uploaded successfully!', 'success');
    } catch (error) {
      console.error('Error uploading logo:', error);
      showNotification(error instanceof Error ? error.message : 'Failed to upload logo', 'error');
    } finally {
      setIsUploadingLogo(false);
    }
  }, [showNotification]);

  const handleReferenceUpload = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    
    setIsUploadingReferences(true);
    try {
      const uploadedAssets = await AdAssetsService.uploadReferenceImages(files);
      setSelectedReferenceImages(prev => [...prev, ...uploadedAssets]);
      showNotification(`${files.length} reference image(s) uploaded successfully!`, 'success');
    } catch (error) {
      console.error('Error uploading reference images:', error);
      showNotification(error instanceof Error ? error.message : 'Failed to upload reference images', 'error');
    } finally {
      setIsUploadingReferences(false);
    }
  }, [showNotification]);

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent, type: 'logo' | 'reference') => {
    e.preventDefault();
    setDragOver(type);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(null);
  };

  const handleDrop = (e: React.DragEvent, type: 'logo' | 'reference') => {
    e.preventDefault();
    setDragOver(null);
    
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    
    if (type === 'logo') {
      handleLogoUpload(files);
    } else {
      handleReferenceUpload(files);
    }
  };

  // Generation handlers
  const handleGenerate = async () => {
    const validation = validateBrief();
    if (!validation.valid) {
      showNotification(validation.errors[0], 'error');
      return;
    }

    if (creditBalance < 1) {
      showNotification('Insufficient credits. Please upgrade your plan.', 'error');
      return;
    }

    setIsGenerating(true);
    navigateToStep('generate');

    // Track ad generation start
    GTMService.trackCustomEvent('start_ad_generation', {
      platform: brief.platform,
      has_logo: !!selectedLogo,
      reference_images_count: selectedReferenceImages.length,
      user_id: user?.id
    });

    try {
      const result = await AdGenerationService.generateAd({
        briefData: brief as AdBrief,
        logoUrl: selectedLogo?.asset_url,
        referenceImageUrls: selectedReferenceImages.map(img => img.asset_url)
      });

      setGenerationResult(result);
      navigateToStep('result');
      setCreditBalance(result.creditsRemaining);
      await loadGenerationHistory();
      
      // Track successful ad generation
      GTMService.trackCustomEvent('complete_ad_generation', {
        platform: brief.platform,
        generation_id: result.generationId,
        credits_consumed: 1,
        user_id: user?.id
      });
      
      showNotification('Ad generated successfully!', 'success');
    } catch (error) {
      console.error('Error generating ad:', error);
      
      // Track ad generation error
      GTMService.trackCustomEvent('ad_generation_error', {
        platform: brief.platform,
        error_message: error instanceof Error ? error.message : 'Unknown error',
        user_id: user?.id
      });
      
      // Provide user-friendly error messages
      let errorMessage = 'Failed to generate ad';
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Add specific help for common errors
        if (error.message.includes('Insufficient credits')) {
          errorMessage = 'Insufficient credits. Please upgrade your plan or purchase credits to continue.';
        } else if (error.message.includes('AI service is not properly configured')) {
          errorMessage = 'The AI service is temporarily unavailable. Please try again later.';
        } else if (error.message.includes('Failed to fetch') || error.message.includes('network')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        }
      }
      
      showNotification(errorMessage, 'error');
      navigateToStep('brief');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerate = async (feedback: string, improvements: string[], newLogo?: AdAsset | null, newReferences?: AdAsset[]) => {
    if (!regeneratingGeneration || creditBalance < 1) {
      setRegenerationError('Cannot regenerate ad. Please check your credit balance.');
      return;
    }

    setIsRegenerating(true);
    setRegenerationError(null); // Clear any previous errors

    try {
      // Get the correct generation ID from either current result or historical generation
      const generation = getGenerationFields(regeneratingGeneration);
      const generationId = generation.id;
      
      // Track ad regeneration start
      GTMService.trackCustomEvent('start_ad_regeneration', {
        generation_id: generationId,
        has_feedback: feedback.length > 0,
        improvements_count: improvements.length,
        has_new_logo: !!newLogo,
        new_references_count: newReferences?.length || 0,
        user_id: user?.id
      });
      
      const result = await AdGenerationService.regenerateAd(
        generationId,
        feedback,
        improvements,
        newLogo,
        newReferences
      );

      // Close modal on success
      resetRegenerationModal();
      
      // Update the current result with the new generation
      setGenerationResult(result);
      setCreditBalance(result.creditsRemaining);
      
      // Switch to result view to show the new generation
      navigateToStep('result');
      
      // Track successful ad regeneration
      GTMService.trackCustomEvent('complete_ad_regeneration', {
        generation_id: result.generationId,
        new_generation_count: result.generationCount,
        credits_consumed: 1,
        user_id: user?.id
      });
      
      // Load history and show notification
      await loadGenerationHistory();
      showNotification(`Ad regenerated successfully! (Generation #${result.generationCount})`, 'success');
      
    } catch (error) {
      console.error('Error regenerating ad:', error);
      
      // Track ad regeneration error
      GTMService.trackCustomEvent('ad_regeneration_error', {
        generation_id: getGenerationFields(regeneratingGeneration).id,
        error_message: error instanceof Error ? error.message : 'Unknown error',
        user_id: user?.id
      });
      
      // Provide user-friendly error messages
      let errorMessage = 'Failed to regenerate your ad. Please try again.';
      if (error instanceof Error) {
        // Add specific help for common errors
        if (error.message.includes('Insufficient') || error.message.includes('credits')) {
          errorMessage = 'You don\'t have enough credits. Please upgrade your plan or purchase additional credits to continue.';
        } else if (error.message.includes('Content policy violation') || error.message.includes('policy violation')) {
          errorMessage = 'Your request couldn\'t be processed due to content guidelines. Try rephrasing your feedback or using different terms.';
        } else if (error.message.includes('AI service') || error.message.includes('not properly configured')) {
          errorMessage = 'The AI service is temporarily unavailable. Please try again in a few moments.';
        } else if (error.message.includes('Failed to fetch') || error.message.includes('network')) {
          errorMessage = 'Connection issue detected. Please check your internet connection and try again.';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'The request took too long. Please try again with simpler modifications.';
        }
      }
      
      // Set error in modal instead of showing global notification
      setRegenerationError(errorMessage);
      // Keep modal open so user can see the error and retry
    } finally {
      setIsRegenerating(false);
    }
  };

  // Action handlers
  const handleDownload = async () => {
    if (!generationResult) return;
    
    try {
      showNotification('Preparing download...', 'info');
      
      // Fetch the image as a blob to handle CORS and Supabase storage properly
      const response = await fetch(generationResult.generatedImageUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch image');
      }
      
      const blob = await response.blob();
      
      // Create blob URL and download
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `rulevia-ad-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up blob URL
      URL.revokeObjectURL(blobUrl);
      
      // Track successful ad download
      GTMService.trackCustomEvent('download_ad', {
        generation_id: generationResult.generationId,
        platform: brief.platform,
        file_format: 'png',
        user_id: user?.id
      });
      
      showNotification('Ad downloaded successfully!', 'success');
    } catch (error) {
      console.error('Download error:', error);
      showNotification('Download failed. Please try again.', 'error');
    }
  };

  // Generic download function for any image URL
  const downloadImage = async (imageUrl: string, filename: string) => {
    try {
      // URLs are now public, can fetch directly
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch image');
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      
      return true;
    } catch (error) {
      console.error('Download error:', error);
      return false;
    }
  };

  // Filter generations function
  const filterGenerations = useCallback((generations: AdGeneration[]) => {
    return generations.filter(gen => {
      // Search filter
      if (historySearchTerm) {
        const search = historySearchTerm.toLowerCase();
        const matchesSearch = 
          gen.brief_data.context?.toLowerCase().includes(search) ||
          gen.brief_data.headline?.toLowerCase().includes(search) ||
          gen.brief_data.platform?.toLowerCase().includes(search) ||
          gen.brief_data.style?.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }
      
      // Platform filter
      if (historyFilters.platform !== 'all') {
        if (gen.brief_data.platform?.toLowerCase() !== historyFilters.platform.toLowerCase()) {
          return false;
        }
      }
      
      // Style filter
      if (historyFilters.style !== 'all') {
        if (gen.brief_data.style?.toLowerCase() !== historyFilters.style.toLowerCase()) {
          return false;
        }
      }
      
      // Size filter
      if (historyFilters.size !== 'all') {
        if (gen.brief_data.size !== historyFilters.size) {
          return false;
        }
      }
      
      // Date range filter
      if (historyFilters.dateRange !== 'all') {
        const genDate = new Date(gen.created_at);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        switch (historyFilters.dateRange) {
          case 'today': {
            if (genDate < today) return false;
            break;
          }
          case 'week': {
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            if (genDate < weekAgo) return false;
            break;
          }
          case 'month': {
            const monthAgo = new Date(today);
            monthAgo.setDate(monthAgo.getDate() - 30);
            if (genDate < monthAgo) return false;
            break;
          }
          case 'custom': {
            if (historyFilters.customDateStart && genDate < historyFilters.customDateStart) return false;
            if (historyFilters.customDateEnd && genDate > historyFilters.customDateEnd) return false;
            break;
          }
        }
      }
      
      return true;
    });
  }, [historySearchTerm, historyFilters]);

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    if (historyFilters.platform !== 'all') count++;
    if (historyFilters.style !== 'all') count++;
    if (historyFilters.size !== 'all') count++;
    if (historyFilters.dateRange !== 'all') count++;
    return count;
  };

  // Clear all filters
  const clearAllFilters = () => {
    setHistorySearchTerm('');
    setHistoryFilters({
      platform: 'all',
      style: 'all',
      size: 'all',
      dateRange: 'all',
      customDateStart: null,
      customDateEnd: null
    });
  };

  const handleAnalyze = async () => {
    if (!generationResult) return;
    
    try {
      // Ensure body scroll is unlocked before navigation
      unlockBodyScroll();
      
      // Convert the generated image URL to a file for the Analysis page
      const response = await fetch(generationResult.generatedImageUrl);
      const blob = await response.blob();
      const file = new File([blob], `generated-ad-${Date.now()}.png`, { type: 'image/png' });
      
      // Navigate to analysis page with the generated ad
      navigate('/dashboard/analysis', { 
        state: { 
          uploadedFile: file,
          preloadedImageUrl: generationResult.generatedImageUrl,
          source: 'ad-generator',
          generationId: generationResult.generationId,
          autoStartAnalysis: true // Flag to automatically start analysis
        }
      });
    } catch (error) {
      console.error('Error preparing ad for analysis:', error);
      showNotification('Failed to start analysis. Please try again.', 'error');
    }
  };


  const steps = [
    { id: 'brief', label: 'Creative Brief', icon: Edit2 },
    { id: 'assets', label: 'Assets', icon: FileImage },
    { id: 'generate', label: 'Generate', icon: Sparkles },
    { id: 'result', label: 'Result', icon: Eye }
  ];

  const styleOptions = [
    'Modern', 'Minimalist', 'Bold', 'Corporate', 'Playful', 'Elegant', 'Retro', 'Futuristic'
  ];

  const platformOptions = [
    'Facebook', 'Instagram', 'Google Ads', 'LinkedIn', 'TikTok', 'Pinterest', 'Twitter', 'YouTube'
  ];

  const sizeOptions = [
    { value: 'auto', label: 'Auto (Default)', description: 'AI optimized' },
    { value: 'square', label: 'Square (1:1)', description: '1024x1024' },
    { value: 'landscape', label: 'Landscape (3:2)', description: '1536x1024' },
    { value: 'portrait', label: 'Portrait (2:3)', description: '1024x1536' }
  ];

  // Helper function to get unified field values from either generation type
  const getGenerationFields = (generation: AdGeneration | AdGenerationResult) => {
    const isResult = 'generationId' in generation;
    
    return {
      id: isResult ? (generation as AdGenerationResult).generationId : (generation as AdGeneration).id,
      imageUrl: isResult ? (generation as AdGenerationResult).generatedImageUrl : (generation as AdGeneration).generated_image_url || '',
      count: isResult ? (generation as AdGenerationResult).generationCount : (generation as AdGeneration).generation_count,
      briefData: isResult ? brief : (generation as AdGeneration).brief_data
    };
  };

  // Regeneration Modal Component
  const RegenerateModal = ({ error, onClearError }: { error: string | null; onClearError: () => void }) => {
    
    // Modal-local asset state - self-contained and independent of parent re-renders
    const [modalLogo, setModalLogo] = useState<AdAsset | null>(null);
    const [modalReferenceImages, setModalReferenceImages] = useState<AdAsset[]>([]);
    const [modalDragOver, setModalDragOver] = useState<'logo' | 'reference' | null>(null);
    const [isModalUploadingLogo, setIsModalUploadingLogo] = useState(false);
    const [isModalUploadingReferences, setIsModalUploadingReferences] = useState(false);
    
    // Asset selection mode states (upload vs library)
    const [logoSelectionMode, setLogoSelectionMode] = useState<'upload' | 'library'>('upload');
    const [referenceSelectionMode, setReferenceSelectionMode] = useState<'upload' | 'library'>('upload');
    
    // Library selection states
    const [showLogoLibrary, setShowLogoLibrary] = useState(false);
    const [showReferenceLibrary, setShowReferenceLibrary] = useState(false);
    const [selectedLibraryLogo, setSelectedLibraryLogo] = useState<AdAsset | null>(null);
    const [selectedLibraryReferences, setSelectedLibraryReferences] = useState<AdAsset[]>([]);
    
    // Use local state to prevent re-renders
    const [localFeedback, setLocalFeedback] = useState(regenerationFeedback);
    const [localImprovements, setLocalImprovements] = useState(() => 
      improvementOptions.map(opt => ({ ...opt, selected: false }))
    );
    const [localLoadingMessageIndex, setLocalLoadingMessageIndex] = useState(0);
    
    // Local notification state for modal
    const [modalNotification, setModalNotification] = useState<{
      message: string;
      type: 'success' | 'error';
      show: boolean;
    }>({ message: '', type: 'success', show: false });

    // Modal-specific notification handler
    const showModalNotification = useCallback((message: string, type: 'success' | 'error' = 'success') => {
      setModalNotification({ message, type, show: true });
      setTimeout(() => setModalNotification(prev => ({ ...prev, show: false })), 3000);
    }, []);

    // Modal-specific asset upload handlers
    const handleModalLogoUpload = useCallback(async (files: File[]) => {
      if (files.length === 0) return;
      
      const file = files[0];
      setIsModalUploadingLogo(true);
      
      try {
        const uploadedAsset = await AdAssetsService.uploadLogo(file);
        setModalLogo(uploadedAsset);
        showModalNotification('Logo uploaded successfully!', 'success');
        // Asset library will be reloaded when modal closes
      } catch (error) {
        console.error('Error uploading logo:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to upload logo';
        showModalNotification(errorMessage, 'error');
      } finally {
        setIsModalUploadingLogo(false);
      }
    }, [showModalNotification]);

    const handleModalReferenceUpload = useCallback(async (files: File[]) => {
      if (files.length === 0) return;
      
      // Check if user already has reference images for regeneration
      if (modalReferenceImages.length >= 1) {
        showModalNotification('Only 1 reference image is allowed for regeneration with Gemini model', 'error');
        return;
      }
      
      setIsModalUploadingReferences(true);
      try {
        // Only upload the first file to respect the 1 image limit
        const filesToUpload = files.slice(0, 1 - modalReferenceImages.length);
        const uploadedAssets = await AdAssetsService.uploadReferenceImages(filesToUpload);
        setModalReferenceImages(prev => [...prev, ...uploadedAssets].slice(0, 1));
        showModalNotification(`${filesToUpload.length} reference image(s) uploaded successfully!`, 'success');
        
        if (files.length > filesToUpload.length) {
          showModalNotification('Only 1 reference image allowed for regeneration - additional images ignored', 'error');
        }
        // Asset library will be reloaded when modal closes
      } catch (error) {
        console.error('Error uploading reference images:', error);
        showModalNotification(error instanceof Error ? error.message : 'Failed to upload reference images', 'error');
      } finally {
        setIsModalUploadingReferences(false);
      }
    }, [showModalNotification, modalReferenceImages.length]);

    // Regeneration drag and drop handlers
    const handleModalDragOver = (e: React.DragEvent, type: 'logo' | 'reference') => {
      e.preventDefault();
      setModalDragOver(type);
    };

    const handleModalDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      setModalDragOver(null);
    };

    const handleModalDrop = (e: React.DragEvent, type: 'logo' | 'reference') => {
      e.preventDefault();
      setModalDragOver(null);
      
      const files = Array.from(e.dataTransfer.files).filter(file => 
        file.type.startsWith('image/')
      );
      
      if (type === 'logo') {
        handleModalLogoUpload(files);
      } else {
        handleModalReferenceUpload(files);
      }
    };

    // Only reset modal assets when modal is closed by user action, not by re-renders

    // Handle loading message rotation inside modal
    useEffect(() => {
      if (isRegenerating) {
        const interval = setInterval(() => {
          setLocalLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
        }, 3000);
        return () => clearInterval(interval);
      }
    }, [isRegenerating]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!regeneratingGeneration) return null;

    // Get the unified generation fields
    const generation = getGenerationFields(regeneratingGeneration);

    const handleSubmitRegeneration = () => {
      const selectedImprovementIds = localImprovements
        .filter(imp => imp.selected)
        .map(imp => imp.label);
      
      if (!localFeedback.trim() && selectedImprovementIds.length === 0) {
        showModalNotification('Please provide feedback or select improvements', 'error');
        return;
      }
      
      // Clear any existing error before trying again
      onClearError();
      
      // Combine uploaded and library-selected assets
      const finalLogo = selectedLibraryLogo || modalLogo;
      const finalReferences = [...modalReferenceImages, ...selectedLibraryReferences];
      
      // Don't close modal immediately - let handleRegenerate handle it
      handleRegenerate(localFeedback, selectedImprovementIds, finalLogo, finalReferences);
    };

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-2 sm:p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget && !isRegenerating) {
            resetRegenerationModal();
          }
        }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-2xl max-w-4xl w-full min-h-0 max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)] overflow-hidden relative flex flex-col"
        >
              {/* Modal Notification */}
              <AnimatePresence>
                {modalNotification.show && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className={`absolute top-4 left-1/2 transform -translate-x-1/2 z-10 px-4 py-2 rounded-lg flex items-center space-x-2 ${
                      modalNotification.type === 'success'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {modalNotification.type === 'success' && <CheckCircle className="w-4 h-4" />}
                    {modalNotification.type === 'error' && <XCircle className="w-4 h-4" />}
                    <span className="text-sm font-medium">{modalNotification.message}</span>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Improve Your Ad</h2>
                    <p className="text-xs sm:text-sm text-gray-500 truncate">
                      Generation #{generation.count}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (!isRegenerating) {
                      resetRegenerationModal();
                    }
                  }}
                  disabled={isRegenerating}
                  className={`p-2 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0 ${
                    isRegenerating 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:bg-gray-100'
                  }`}
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 overflow-y-auto flex-1 min-h-0">
                {/* Error Display */}
                <AnimatePresence>
                  {error && !isRegenerating && (
                    <motion.div
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="mb-6"
                    >
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <XCircle className="h-5 w-5 text-red-400 mt-0.5" />
                          </div>
                          <div className="ml-3 flex-1">
                            <h3 className="text-sm font-medium text-red-800">
                              Regeneration Failed
                            </h3>
                            <div className="mt-2 text-sm text-red-700">
                              <p>{error}</p>
                            </div>
                            {error.includes('credits') && (
                              <div className="mt-3">
                                <button
                                  onClick={() => navigate('/pricing')}
                                  className="text-sm font-medium text-red-800 hover:text-red-700 underline"
                                >
                                  View pricing plans →
                                </button>
                              </div>
                            )}
                            {error.includes('content guidelines') && (
                              <div className="mt-3 text-xs text-red-600">
                                <p className="font-medium">Tips to avoid this:</p>
                                <ul className="mt-1 list-disc list-inside space-y-1">
                                  <li>Use professional language</li>
                                  <li>Avoid sensitive topics</li>
                                  <li>Keep feedback constructive</li>
                                </ul>
                              </div>
                            )}
                          </div>
                          <div className="ml-auto pl-3">
                            <button
                              onClick={onClearError}
                              className="inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50"
                            >
                              <span className="sr-only">Dismiss</span>
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {isRegenerating ? (
                  /* Loading State */
                  <div className="text-center py-12">
                    {/* Animated loader */}
                    <div className="relative mb-8">
                      <div className="w-32 h-32 rounded-full bg-purple-100 flex items-center justify-center mx-auto">
                        <RotateCcw className="w-16 h-16 text-purple-600 animate-pulse" />
                      </div>
                      <div className="absolute inset-0 w-32 h-32 rounded-full border-4 border-purple-600 border-t-transparent animate-spin mx-auto" />
                    </div>
                    
                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                      Improving Your Ad...
                    </h2>
                    
                    {/* Rotating message */}
                    <motion.p
                      key={localLoadingMessageIndex}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="text-lg text-gray-700 mb-8"
                    >
                      {loadingMessages[localLoadingMessageIndex]}
                    </motion.p>
                    
                    <p className="text-sm text-gray-500 mb-8">
                      This usually takes 1-5 minutes
                    </p>
                    
                    <div className="max-w-md mx-auto bg-gray-50 rounded-lg p-4">
                      <div className="text-sm text-gray-700 space-y-2">
                        <div>
                          <span className="font-medium">Applying feedback:</span>
                          <p className="text-xs mt-1 text-gray-600">
                            {localFeedback || 'Using selected improvements'}
                          </p>
                        </div>
                        <div className="flex justify-between">
                          <span>Generation:</span>
                          <span className="font-medium">#{generation.count + 1}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Normal Form State */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    {/* Current Ad Preview */}
                    <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900">Current Ad</h3>
                    <div className="bg-gray-100 rounded-lg overflow-hidden">
                      <SecureImage
                        src={generation.imageUrl}
                        alt="Current generated ad"
                        className="w-full h-auto"
                      />
                    </div>
                    
                    {/* Ad Details */}
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Platform:</span>
                        <span className="font-medium">{generation.briefData?.platform}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Style:</span>
                        <span className="font-medium">{generation.briefData?.style}</span>
                      </div>
                      {generation.briefData?.headline && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Headline:</span>
                          <span className="font-medium">{generation.briefData.headline}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Improvement Options */}
                  <div className="space-y-4">
                    {/* Feedback Text Area */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        What would you like to change?
                      </label>
                      <textarea
                        value={localFeedback}
                        onChange={(e) => setLocalFeedback(e.target.value)}
                        onPaste={(e) => e.stopPropagation()}
                        onCopy={(e) => e.stopPropagation()}
                        placeholder="Describe specific changes you'd like to see..."
                        className="w-full px-4 py-3 min-h-[80px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-base touch-manipulation"
                        rows={4}
                        maxLength={500}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {localFeedback.length}/500 characters
                      </p>
                    </div>

                    {/* Improvement Checkboxes */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Select areas to improve
                      </label>
                      <div className="space-y-2">
                        {localImprovements.map((improvement) => (
                          <label
                            key={improvement.id}
                            className="flex items-start space-x-3 p-3 min-h-[44px] border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors touch-manipulation"
                          >
                            <input
                              type="checkbox"
                              checked={improvement.selected}
                              onChange={(e) => {
                                setLocalImprovements(prev =>
                                  prev.map(imp =>
                                    imp.id === improvement.id
                                      ? { ...imp, selected: e.target.checked }
                                      : imp
                                  )
                                );
                              }}
                              className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded touch-manipulation"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{improvement.label}</div>
                              <div className="text-sm text-gray-500">{improvement.description}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Quick Presets */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Quick improvements
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <button
                          onClick={() => {
                            setLocalFeedback('Make it look more professional and corporate');
                            setLocalImprovements(prev =>
                              prev.map(imp =>
                                ['style', 'colors'].includes(imp.id)
                                  ? { ...imp, selected: true }
                                  : imp
                              )
                            );
                          }}
                          className="px-3 py-2 min-h-[44px] text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors touch-manipulation"
                        >
                          More Professional
                        </button>
                        <button
                          onClick={() => {
                            setLocalFeedback('Make it more fun and playful');
                            setLocalImprovements(prev =>
                              prev.map(imp =>
                                ['style', 'colors'].includes(imp.id)
                                  ? { ...imp, selected: true }
                                  : imp
                              )
                            );
                          }}
                          className="px-3 py-2 min-h-[44px] text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors touch-manipulation"
                        >
                          More Playful
                        </button>
                        <button
                          onClick={() => {
                            setLocalFeedback('Increase contrast for better readability');
                            setLocalImprovements(prev =>
                              prev.map(imp =>
                                ['colors', 'text'].includes(imp.id)
                                  ? { ...imp, selected: true }
                                  : imp
                              )
                            );
                          }}
                          className="px-3 py-2 min-h-[44px] text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors touch-manipulation"
                        >
                          Increase Contrast
                        </button>
                        <button
                          onClick={() => {
                            setLocalFeedback('Simplify the design and reduce clutter');
                            setLocalImprovements(prev =>
                              prev.map(imp =>
                                ['layout', 'style'].includes(imp.id)
                                  ? { ...imp, selected: true }
                                  : imp
                              )
                            );
                          }}
                          className="px-3 py-2 min-h-[44px] text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors touch-manipulation"
                        >
                          Simplify Design
                        </button>
                      </div>
                    </div>

                    {/* Asset Upload Section */}
                    <div className="border-t pt-6 mt-6">
                      <h4 className="text-sm font-medium text-gray-700 mb-4 flex items-center">
                        <FileImage className="w-4 h-4 mr-2" />
                        Add New Assets (Optional)
                      </h4>
                      <p className="text-xs text-gray-500 mb-4">
                        Upload new logo and reference images to be merged with the original assets for better results.
                      </p>
                      
                      <div className="space-y-4">
                        {/* Logo Upload for Regeneration */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <label className="text-sm font-medium text-gray-600">
                              New Logo (Optional)
                            </label>
                            
                            {/* Toggle buttons for upload/library mode */}
                            <div className="flex bg-gray-100 rounded-lg p-1">
                              <button
                                onClick={() => setLogoSelectionMode('upload')}
                                className={`px-3 py-2 min-h-[36px] text-xs font-medium rounded transition-colors touch-manipulation ${
                                  logoSelectionMode === 'upload'
                                    ? 'bg-white text-purple-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-800'
                                }`}
                              >
                                <Upload className="w-3 h-3 inline mr-1 flex-shrink-0" />
                                Upload New
                              </button>
                              <button
                                onClick={() => setLogoSelectionMode('library')}
                                className={`px-3 py-2 min-h-[36px] text-xs font-medium rounded transition-colors touch-manipulation ${
                                  logoSelectionMode === 'library'
                                    ? 'bg-white text-purple-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-800'
                                }`}
                              >
                                <Image className="w-3 h-3 inline mr-1 flex-shrink-0" />
                                From Library
                              </button>
                            </div>
                          </div>
                          
                          {logoSelectionMode === 'upload' ? (
                          <div
                            onDragOver={(e) => handleModalDragOver(e, 'logo')}
                            onDragLeave={handleModalDragLeave}
                            onDrop={(e) => handleModalDrop(e, 'logo')}
                            className={`
                              relative border-2 border-dashed rounded-lg p-4 text-center transition-all duration-300
                              ${modalDragOver === 'logo' ? 'border-purple-400 bg-purple-50 scale-105' : 
                                modalLogo ? 'border-green-300 bg-green-50' : 
                                'border-gray-300 hover:border-purple-300 hover:bg-purple-50/30'}
                            `}
                          >
                            {isModalUploadingLogo ? (
                              <div className="flex flex-col items-center justify-center space-y-2">
                                <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                                <p className="text-xs text-gray-600">Uploading...</p>
                              </div>
                            ) : modalLogo ? (
                              <div className="flex items-center justify-center space-x-3">
                                <img
                                  src={modalLogo.asset_url}
                                  alt="Uploaded logo"
                                  className="w-12 h-12 object-contain rounded"
                                />
                                <div className="flex-1 text-left">
                                  <p className="text-sm font-medium text-gray-900">{modalLogo.original_filename}</p>
                                  <p className="text-xs text-gray-500">Logo uploaded</p>
                                </div>
                                <button
                                  onClick={() => setModalLogo(null)}
                                  className="p-2 min-w-[36px] min-h-[36px] flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors touch-manipulation"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <>
                                <FileImage className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                <div className="space-y-1">
                                  <p className="text-sm text-gray-600">
                                    Drop logo here or click to browse
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    PNG, JPG, SVG up to 10MB
                                  </p>
                                </div>
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handleModalLogoUpload(Array.from(e.target.files || []))}
                                  disabled={isModalUploadingLogo}
                                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                                />
                              </>
                            )}
                          </div>
                          ) : (
                            /* Library Selection Mode for Logo */
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                              {selectedLibraryLogo ? (
                                <div className="flex items-center justify-center space-x-3">
                                  <img
                                    src={selectedLibraryLogo.asset_url}
                                    alt="Selected library logo"
                                    className="w-12 h-12 object-contain rounded"
                                  />
                                  <div className="flex-1 text-left">
                                    <p className="text-sm font-medium text-gray-900">{selectedLibraryLogo.original_filename}</p>
                                    <p className="text-xs text-gray-500">Selected from library</p>
                                  </div>
                                  <button
                                    onClick={() => setSelectedLibraryLogo(null)}
                                    className="p-2 min-w-[36px] min-h-[36px] flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors touch-manipulation"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                <div className="text-center">
                                  <Image className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                  <button
                                    onClick={() => setShowLogoLibrary(true)}
                                    className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                                  >
                                    Browse Logo Library
                                  </button>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Select from your previously uploaded logos
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Reference Images Upload for Regeneration */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <label className="text-sm font-medium text-gray-600">
                              New Reference Image (Optional)
                            </label>
                            
                            {/* Toggle buttons for upload/library mode */}
                            <div className="flex bg-gray-100 rounded-lg p-1">
                              <button
                                onClick={() => setReferenceSelectionMode('upload')}
                                className={`px-3 py-2 min-h-[36px] text-xs font-medium rounded transition-colors touch-manipulation ${
                                  referenceSelectionMode === 'upload'
                                    ? 'bg-white text-purple-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-800'
                                }`}
                              >
                                <Upload className="w-3 h-3 inline mr-1 flex-shrink-0" />
                                Upload New
                              </button>
                              <button
                                onClick={() => setReferenceSelectionMode('library')}
                                className={`px-3 py-2 min-h-[36px] text-xs font-medium rounded transition-colors touch-manipulation ${
                                  referenceSelectionMode === 'library'
                                    ? 'bg-white text-purple-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-800'
                                }`}
                              >
                                <Image className="w-3 h-3 inline mr-1 flex-shrink-0" />
                                From Library
                              </button>
                            </div>
                          </div>
                          
                          {referenceSelectionMode === 'upload' ? (
                          <div
                            onDragOver={(e) => handleModalDragOver(e, 'reference')}
                            onDragLeave={handleModalDragLeave}
                            onDrop={(e) => handleModalDrop(e, 'reference')}
                            className={`
                              relative border-2 border-dashed rounded-lg p-4 text-center transition-all duration-300
                              ${modalDragOver === 'reference' ? 'border-purple-400 bg-purple-50 scale-105' : 
                                modalReferenceImages.length > 0 ? 'border-green-300 bg-green-50' : 
                                'border-gray-300 hover:border-purple-300 hover:bg-purple-50/30'}
                            `}
                          >
                            {isModalUploadingReferences ? (
                              <div className="flex flex-col items-center justify-center space-y-2 py-4">
                                <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                                <p className="text-xs text-gray-600">Uploading images...</p>
                              </div>
                            ) : modalReferenceImages.length > 0 ? (
                              <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-2">
                                  {modalReferenceImages.map((image, index) => (
                                    <div key={image.id} className="relative group">
                                      <img
                                        src={image.asset_url}
                                        alt={`Reference ${index + 1}`}
                                        className="w-full h-16 object-cover rounded"
                                      />
                                      <button
                                        onClick={() => {
                                          setModalReferenceImages(prev => 
                                            prev.filter(img => img.id !== image.id)
                                          );
                                        }}
                                        className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity touch-manipulation"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                                
                                {modalReferenceImages.length < 1 && (
                                  <div className="border-t pt-3">
                                    <input
                                      type="file"
                                      accept="image/*"
                                      multiple
                                      onChange={(e) => handleModalReferenceUpload(Array.from(e.target.files || []))}
                                      disabled={isModalUploadingReferences}
                                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                                    />
                                    <p className="text-xs text-gray-600">
                                      Drop image or click to add ({modalReferenceImages.length}/1)
                                    </p>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <>
                                <FileImage className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                <div className="space-y-1">
                                  <p className="text-sm text-gray-600">
                                    Drop reference image here or click to browse
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    PNG, JPG, GIF up to 20MB (1 image only)
                                  </p>
                                </div>
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  onChange={(e) => handleModalReferenceUpload(Array.from(e.target.files || []))}
                                  disabled={isModalUploadingReferences}
                                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                                />
                              </>
                            )}
                          </div>
                          ) : (
                            /* Library Selection Mode for References */
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                              {selectedLibraryReferences.length > 0 ? (
                                <div className="space-y-3">
                                  <div className="grid grid-cols-2 gap-2">
                                    {selectedLibraryReferences.map((image, index) => (
                                      <div key={image.id} className="relative group">
                                        <img
                                          src={image.asset_url}
                                          alt={`Library reference ${index + 1}`}
                                          className="w-full h-16 object-cover rounded"
                                        />
                                        <button
                                          onClick={() => {
                                            setSelectedLibraryReferences(prev => 
                                              prev.filter(img => img.id !== image.id)
                                            );
                                          }}
                                          className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity touch-manipulation"
                                        >
                                          <X className="w-3 h-3" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                  
                                  {selectedLibraryReferences.length < 1 && (
                                    <div className="border-t pt-3">
                                      <button
                                        onClick={() => setShowReferenceLibrary(true)}
                                        className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                                      >
                                        Add from library ({selectedLibraryReferences.length}/1)
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="text-center">
                                  <FileImage className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                  <button
                                    onClick={() => setShowReferenceLibrary(true)}
                                    className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                                  >
                                    Browse Reference Library
                                  </button>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Select from your previously uploaded references
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 sm:p-6 border-t border-gray-200 flex-shrink-0">
                {!isRegenerating ? (
                  <>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Zap className="w-4 h-4 text-yellow-500" />
                      <span>This will use 1 credit</span>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                      <button
                        onClick={() => {
                          resetRegenerationModal();
                        }}
                        className="px-4 py-2 min-h-[44px] border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors touch-manipulation"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSubmitRegeneration}
                        disabled={creditBalance < 1}
                        className="px-4 py-2 min-h-[44px] bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 touch-manipulation"
                      >
                        <RotateCcw className="w-4 h-4" />
                        <span>{error ? 'Try Again' : 'Regenerate Ad'}</span>
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="w-full text-center">
                    <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                      <RotateCcw className="w-4 h-4 animate-spin text-purple-600" />
                      <span>Regeneration in progress...</span>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
            
            {/* Logo Library Selection Modal */}
            {showLogoLibrary && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-2 sm:p-4"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setShowLogoLibrary(false);
                  }
                }}
              >
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  className="bg-white rounded-lg sm:rounded-xl shadow-2xl max-w-2xl w-full min-h-0 max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)] overflow-hidden flex flex-col"
                >
                  {/* Library Modal Header */}
                  <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
                      <Image className="w-5 h-5 text-purple-600 mr-2" />
                      Select Logo from Library
                    </h3>
                    <button
                      onClick={() => setShowLogoLibrary(false)}
                      className="p-2 min-w-[44px] min-h-[44px] hover:bg-gray-100 rounded-lg transition-colors touch-manipulation"
                    >
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>
                  
                  {/* Library Content */}
                  <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0">
                    {assetLibrary.logos.length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                        {assetLibrary.logos.map((logo) => (
                          <button
                            key={logo.id}
                            onClick={() => {
                              setSelectedLibraryLogo(logo);
                              setShowLogoLibrary(false);
                            }}
                            className={`relative group border-2 rounded-lg p-3 sm:p-4 min-h-[88px] transition-all hover:border-purple-400 hover:shadow-md touch-manipulation ${
                              selectedLibraryLogo?.id === logo.id ? 'border-purple-600 bg-purple-50' : 'border-gray-200'
                            }`}
                          >
                            <img
                              src={logo.asset_url}
                              alt={logo.original_filename}
                              className="w-full h-12 sm:h-20 object-contain mb-2"
                            />
                            <p className="text-xs text-gray-600 truncate">{logo.original_filename}</p>
                            <div className="absolute inset-0 bg-purple-600 bg-opacity-0 group-hover:bg-opacity-5 rounded-lg transition-all" />
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Image className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No logos in your library yet</p>
                        <p className="text-sm text-gray-400 mt-1">Upload logos and they'll appear here</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Library Modal Footer */}
                  <div className="flex justify-end p-4 border-t border-gray-200 flex-shrink-0">
                    <button
                      onClick={() => setShowLogoLibrary(false)}
                      className="px-4 py-2 min-h-[44px] text-gray-600 hover:text-gray-800 transition-colors touch-manipulation"
                    >
                      Close
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
            
            {/* Reference Library Selection Modal */}
            {showReferenceLibrary && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-2 sm:p-4"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setShowReferenceLibrary(false);
                  }
                }}
              >
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  className="bg-white rounded-lg sm:rounded-xl shadow-2xl max-w-3xl w-full min-h-0 max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)] overflow-hidden flex flex-col"
                >
                  {/* Library Modal Header */}
                  <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
                      <FileImage className="w-5 h-5 text-purple-600 mr-2" />
                      Select Reference Images from Library
                    </h3>
                    <button
                      onClick={() => setShowReferenceLibrary(false)}
                      className="p-2 min-w-[44px] min-h-[44px] hover:bg-gray-100 rounded-lg transition-colors touch-manipulation"
                    >
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>
                  
                  {/* Library Content */}
                  <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0">
                    {assetLibrary.referenceImages.length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
                        {assetLibrary.referenceImages.map((image) => {
                          const isSelected = selectedLibraryReferences.some(ref => ref.id === image.id);
                          const canAdd = selectedLibraryReferences.length < 1;
                          
                          return (
                            <button
                              key={image.id}
                              onClick={() => {
                                if (isSelected) {
                                  setSelectedLibraryReferences(prev => 
                                    prev.filter(ref => ref.id !== image.id)
                                  );
                                } else if (canAdd) {
                                  setSelectedLibraryReferences(prev => [...prev, image]);
                                }
                              }}
                              disabled={!isSelected && !canAdd}
                              className={`relative group border-2 rounded-lg overflow-hidden transition-all touch-manipulation ${
                                isSelected 
                                  ? 'border-purple-600 ring-2 ring-purple-400' 
                                  : canAdd
                                    ? 'border-gray-200 hover:border-purple-400 hover:shadow-md'
                                    : 'border-gray-200 opacity-50 cursor-not-allowed'
                              }`}
                            >
                              <img
                                src={image.asset_url}
                                alt={image.original_filename}
                                className="w-full h-20 sm:h-24 object-cover"
                              />
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                                <p className="text-xs text-white truncate">{image.original_filename}</p>
                              </div>
                              {isSelected && (
                                <div className="absolute top-2 right-2 w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center">
                                  <CheckCircle className="w-4 h-4 text-white" />
                                </div>
                              )}
                              <div className={`absolute inset-0 bg-purple-600 bg-opacity-0 transition-all ${
                                canAdd || isSelected ? 'group-hover:bg-opacity-10' : ''
                              }`} />
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <FileImage className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No reference images in your library yet</p>
                        <p className="text-sm text-gray-400 mt-1">Upload reference images and they'll appear here</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Library Modal Footer */}
                  <div className="flex items-center justify-between p-4 border-t border-gray-200 flex-shrink-0">
                    <p className="text-sm text-gray-600">
                      Selected: {selectedLibraryReferences.length}/1 image
                    </p>
                    <button
                      onClick={() => setShowReferenceLibrary(false)}
                      className="px-4 py-2 min-h-[44px] bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors touch-manipulation"
                    >
                      Done
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </motion.div>
    );
  };

  // Creative View Modal Component
  const CreativeViewModal = () => {
    if (!selectedCreative || !showCreativeModal) return null;

    return (
      <AnimatePresence>
        {showCreativeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                unlockBodyScroll();
                setShowCreativeModal(false);
                setSelectedCreative(null);
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg sm:rounded-xl shadow-2xl max-w-4xl w-full min-h-0 max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)] overflow-hidden flex flex-col"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Eye className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">View Generated Creative</h2>
                    <p className="text-sm text-gray-500">
                      {selectedCreative.brief_data.platform} • {selectedCreative.brief_data.style}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    unlockBodyScroll();
                    setShowCreativeModal(false);
                    setSelectedCreative(null);
                  }}
                  className="p-2 min-w-[44px] min-h-[44px] hover:bg-gray-100 rounded-lg transition-colors touch-manipulation"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  {/* Creative Image */}
                  <div className="space-y-4">
                    <div className="bg-gray-100 rounded-lg overflow-hidden">
                      {selectedCreative.generated_image_url && (
                        <SecureImage
                          src={selectedCreative.generated_image_url}
                          alt="Generated creative"
                          className="w-full h-auto"
                        />
                      )}
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-col space-y-3">
                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={async () => {
                            if (selectedCreative.generated_image_url) {
                              const success = await downloadImage(
                                selectedCreative.generated_image_url,
                                `rulevia-ad-${selectedCreative.id}.png`
                              );
                              if (success) {
                                showNotification('Ad downloaded successfully!', 'success');
                              } else {
                                showNotification('Download failed. Please try again.', 'error');
                              }
                            }
                          }}
                          className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 min-h-[44px] bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors touch-manipulation"
                        >
                          <Download className="w-4 h-4" />
                          <span>Download</span>
                        </button>
                        
                        <button
                          onClick={async () => {
                            if (selectedCreative.generated_image_url) {
                              try {
                                // Ensure body scroll is unlocked before navigation
                                unlockBodyScroll();
                                
                                // Convert the generated image URL to a file for the Analysis page
                                const response = await fetch(selectedCreative.generated_image_url);
                                const blob = await response.blob();
                                const file = new File([blob], `generated-ad-${Date.now()}.png`, { type: 'image/png' });
                                
                                // Navigate to Analysis page with the image
                                navigate('/dashboard/analysis', { 
                                  state: { 
                                    uploadedFile: file,
                                    preloadedImageUrl: selectedCreative.generated_image_url,
                                    source: 'ad-generator-history',
                                    generationId: selectedCreative.id,
                                    autoStartAnalysis: true
                                  } 
                                });
                              } catch (error) {
                                console.error('Error preparing ad for analysis:', error);
                                showNotification('Failed to start analysis. Please try again.', 'error');
                              }
                            }
                          }}
                          className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 min-h-[44px] bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors touch-manipulation"
                        >
                          <FileSearch className="w-4 h-4" />
                          <span>Make an Analysis</span>
                        </button>
                      </div>
                      
                      {/* Regenerate Button */}
                      <button
                        onClick={() => {
                          setRegeneratingGeneration(selectedCreative);
                          lockBodyScroll();
                          setShowRegenerateModal(true);
                          setShowCreativeModal(false);
                        }}
                        disabled={creditBalance < 1}
                        className="w-full flex items-center justify-center space-x-2 px-4 py-3 min-h-[44px] bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600 touch-manipulation"
                      >
                        <RotateCcw className="w-4 h-4" />
                        <span>Regenerate with Feedback</span>
                        <span className="text-xs bg-white bg-opacity-20 px-2 py-1 rounded-full">
                          1 credit
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Creative Details */}
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Creative Details</h3>
                      <div className="space-y-3">
                        {selectedCreative.brief_data.headline && (
                          <div>
                            <label className="text-sm font-medium text-gray-500">Headline</label>
                            <p className="text-gray-900">{selectedCreative.brief_data.headline}</p>
                          </div>
                        )}
                        
                        <div>
                          <label className="text-sm font-medium text-gray-500">Context</label>
                          <p className="text-gray-900">{selectedCreative.brief_data.context}</p>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-sm font-medium text-gray-500">Platform</label>
                            <p className="text-gray-900">{selectedCreative.brief_data.platform}</p>
                          </div>
                          
                          <div>
                            <label className="text-sm font-medium text-gray-500">Style</label>
                            <p className="text-gray-900">{selectedCreative.brief_data.style}</p>
                          </div>
                        </div>
                        
                        
                        {selectedCreative.brief_data.cta && (
                          <div>
                            <label className="text-sm font-medium text-gray-500">Call to Action</label>
                            <p className="text-gray-900">{selectedCreative.brief_data.cta}</p>
                          </div>
                        )}
                        
                        <div className="pt-3 border-t border-gray-200">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">Generation #</span>
                            <span className="font-medium text-gray-900">#{selectedCreative.generation_count}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm mt-2">
                            <span className="text-gray-500">Created</span>
                            <span className="font-medium text-gray-900">
                              {new Date(selectedCreative.created_at).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <Helmet>
        <title>Ad Generator - Rulevia</title>
      </Helmet>
      {/* Notification Banner */}
      <AnimatePresence>
          {notification.show && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`max-w-4xl mx-auto mb-4 sm:mb-6 p-3 sm:p-4 rounded-lg border flex items-start sm:items-center space-x-2 sm:space-x-3 ${
                notification.type === 'success'
                  ? 'bg-green-50 border-green-200 text-green-800'
                  : notification.type === 'error'
                  ? 'bg-red-50 border-red-200 text-red-800'
                  : 'bg-blue-50 border-blue-200 text-blue-800'
              }`}
            >
              {notification.type === 'success' && <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0 mt-0.5 sm:mt-0" />}
              {notification.type === 'error' && <XCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-600 flex-shrink-0 mt-0.5 sm:mt-0" />}
              {notification.type === 'info' && <Info className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 flex-shrink-0 mt-0.5 sm:mt-0" />}
              <span className="flex-1 text-sm sm:text-base">{notification.message}</span>
              <button
                onClick={() => setNotification(prev => ({ ...prev, show: false }))}
                className="text-gray-500 hover:text-gray-700 flex-shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center -m-2"
              >
                <X className="h-4 w-4" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Header */}
        <div className="max-w-4xl mx-auto mb-6 sm:mb-8">
          <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
            <div className="flex items-start">
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 flex items-center flex-wrap">
                  <Sparkles className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 text-purple-600 mr-2 sm:mr-3 flex-shrink-0" />
                  <span>Ad Generator</span>
                </h1>
                <p className="text-sm sm:text-base text-gray-600 mt-1">
                  Create high-quality ads using AI with your brief, logo, and reference images
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4 flex-shrink-0">
              {/* Show Create New Ad button only when viewing results */}
              {activeStep === 'result' && generationResult && (
                <button
                  onClick={createNewAd}
                  className="bg-purple-600 text-white px-3 sm:px-4 py-2 rounded-lg shadow-sm hover:bg-purple-700 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                >
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <Plus className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm font-medium hidden sm:inline">New Ad</span>
                  </div>
                </button>
              )}
              
              <button
                onClick={() => {
                  const historySection = document.getElementById('generation-history');
                  if (historySection) {
                    historySection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="bg-white px-3 sm:px-4 py-3 sm:py-2 rounded-lg border border-gray-200 shadow-sm hover:bg-gray-50 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <History className="h-4 w-4 text-purple-600 flex-shrink-0" />
                  <span className="text-sm font-medium hidden sm:inline">History</span>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="max-w-4xl mx-auto mb-6 sm:mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
            {/* Mobile: Vertical layout */}
            <div className="block sm:hidden">
              <div className="space-y-4">
                {steps.map((step, index) => {
                  const isActive = step.id === activeStep;
                  const isCompleted = steps.findIndex(s => s.id === activeStep) > index;
                  const Icon = step.icon;
                  
                  return (
                    <motion.div 
                      key={step.id} 
                      className="relative flex items-center"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className={`
                        flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300 shadow-sm flex-shrink-0 relative
                        ${isActive ? 'border-purple-600 bg-purple-600 text-white' : 
                          isCompleted ? 'border-green-500 bg-green-500 text-white' : 
                          'border-gray-300 bg-white text-gray-400'}
                      `}>
                        {isCompleted ? (
                          <CheckCircle className="h-5 w-5" />
                        ) : (
                          <Icon className="h-5 w-5" />
                        )}
                      </div>
                      <div className="ml-3 flex-1">
                        <span className={`text-sm font-medium block ${
                          isActive ? 'text-purple-600' : 
                          isCompleted ? 'text-green-600' : 
                          'text-gray-500'
                        }`}>
                          {step.label}
                        </span>
                        {isActive && (
                          <span className="text-xs text-gray-500 mt-0.5 block">Current step</span>
                        )}
                      </div>
                      
                      {index < steps.length - 1 && (
                        <div className={`absolute left-5 top-10 w-0.5 h-8 transition-colors duration-300 ${
                          isCompleted ? 'bg-green-500' : 'bg-gray-300'
                        }`} />
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Desktop: Horizontal layout */}
            <div className="hidden sm:flex items-center justify-center space-x-4 lg:space-x-8">
              {steps.map((step, index) => {
                const isActive = step.id === activeStep;
                const isCompleted = steps.findIndex(s => s.id === activeStep) > index;
                const Icon = step.icon;
                
                return (
                  <React.Fragment key={step.id}>
                    <motion.div 
                      className="flex flex-col items-center"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className={`
                        flex items-center justify-center w-10 h-10 lg:w-12 lg:h-12 rounded-full border-2 transition-all duration-300 shadow-sm
                        ${isActive ? 'border-purple-600 bg-purple-600 text-white scale-110' : 
                          isCompleted ? 'border-green-500 bg-green-500 text-white' : 
                          'border-gray-300 bg-white text-gray-400'}
                      `}>
                        {isCompleted ? (
                          <CheckCircle className="h-5 w-5 lg:h-6 lg:w-6" />
                        ) : (
                          <Icon className="h-5 w-5 lg:h-6 lg:w-6" />
                        )}
                      </div>
                      <span className={`mt-2 text-xs sm:text-sm font-medium text-center ${
                        isActive ? 'text-purple-600' : 
                        isCompleted ? 'text-green-600' : 
                        'text-gray-500'
                      }`}>
                        {step.label}
                      </span>
                    </motion.div>
                    
                    {index < steps.length - 1 && (
                      <div className={`w-8 lg:w-16 h-0.5 transition-colors duration-300 ${
                        isCompleted ? 'bg-green-500' : 'bg-gray-300'
                      }`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            {/* Step 1: Creative Brief */}
            {activeStep === 'brief' && (
              <motion.div
                key="brief"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6 lg:p-8"
              >
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
                    <Edit2 className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 mr-2 flex-shrink-0" />
                    <span>Creative Brief</span>
                  </h2>
                  
                  <div className="space-y-4 sm:space-y-6">
                    {/* Context */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Creative Brief / Context *
                      </label>
                      <textarea
                        value={brief.context || ''}
                        onChange={(e) => setBrief(prev => ({ ...prev, context: e.target.value }))}
                        onPaste={(e) => {
                          // Ensure paste is not blocked
                          e.stopPropagation();
                        }}
                        onCopy={(e) => {
                          // Ensure copy is not blocked
                          e.stopPropagation();
                        }}
                        placeholder="Describe what you want to create. Be specific about style, mood, and purpose. Example: 'Create a YouTube thumbnail for tech review with bright colors' or 'Design a Facebook ad for fitness app'. Uploaded assets will be automatically incorporated."
                        className="w-full px-3 sm:px-4 py-3 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-sm sm:text-base min-h-[120px] sm:min-h-[140px]"
                        rows={5}
                        maxLength={1000}
                      />
                      <div className="mt-1 space-y-1">
                        <p className="text-xs text-gray-500">
                          {brief.context?.length || 0}/1000 characters (minimum 50)
                        </p>
                        {(selectedLogo || selectedReferenceImages.length > 0) && (
                          <p className="text-xs text-green-600 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            Your uploaded assets will be automatically integrated into the generated ad
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Headline */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Headline (Optional)
                      </label>
                      <input
                        type="text"
                        value={brief.headline || ''}
                        onChange={(e) => setBrief(prev => ({ ...prev, headline: e.target.value }))}
                        onPaste={(e) => e.stopPropagation()}
                        onCopy={(e) => e.stopPropagation()}
                        placeholder="Add a headline if needed for your creative..."
                        className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base min-h-[44px]"
                        maxLength={100}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {brief.headline?.length || 0}/100 characters • Leave blank for non-ad creatives
                      </p>
                    </div>

                    {/* CTA */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Call-to-Action (Optional)
                      </label>
                      <input
                        type="text"
                        value={brief.cta || ''}
                        onChange={(e) => setBrief(prev => ({ ...prev, cta: e.target.value }))}
                        onPaste={(e) => e.stopPropagation()}
                        onCopy={(e) => e.stopPropagation()}
                        placeholder="Shop Now, Learn More, Get Started..."
                        className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base min-h-[44px]"
                        maxLength={50}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {brief.cta?.length || 0}/50 characters • Leave blank for non-ad creatives
                      </p>
                    </div>

                    {/* Sticker Text */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sticker Text (Optional)
                      </label>
                      <input
                        type="text"
                        value={brief.stickerText || ''}
                        onChange={(e) => setBrief(prev => ({ ...prev, stickerText: e.target.value }))}
                        onPaste={(e) => e.stopPropagation()}
                        onCopy={(e) => e.stopPropagation()}
                        placeholder="Sale, New, 50% Off..."
                        className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base min-h-[44px]"
                        maxLength={30}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {brief.stickerText?.length || 0}/30 characters • Leave blank if not needed
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      {/* Style */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Style *
                        </label>
                        <select
                          value={brief.style || ''}
                          onChange={(e) => setBrief(prev => ({ ...prev, style: e.target.value }))}
                          className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base min-h-[44px]"
                        >
                          {styleOptions.map(style => (
                            <option key={style} value={style}>{style}</option>
                          ))}
                        </select>
                      </div>

                      {/* Platform */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Platform *
                        </label>
                        <select
                          value={brief.platform || ''}
                          onChange={(e) => setBrief(prev => ({ ...prev, platform: e.target.value }))}
                          className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base min-h-[44px]"
                        >
                          {platformOptions.map(platform => (
                            <option key={platform} value={platform}>{platform}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Size */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Size & Format *
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {sizeOptions.map(size => (
                          <label key={size.value} className="cursor-pointer min-h-[44px]">
                            <input
                              type="radio"
                              name="size"
                              value={size.value}
                              checked={brief.size === size.value}
                              onChange={(e) => setBrief(prev => ({ ...prev, size: e.target.value }))}
                              className="absolute opacity-0 pointer-events-none"
                            />
                            <div className={`
                              p-3 sm:p-4 border-2 rounded-lg transition-all min-h-[44px] flex flex-col justify-center
                              ${brief.size === size.value ? 
                                'border-purple-500 bg-purple-50' : 
                                'border-gray-200 hover:border-gray-300'
                              }
                            `}>
                              <div className="font-medium text-sm sm:text-base">{size.label}</div>
                              <div className="text-xs sm:text-sm text-gray-500 mt-1">{size.description}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Colors */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Color Palette (Optional)
                      </label>
                      <div className="space-y-4">
                        {/* AI Choice Option */}
                        <label className="flex items-start sm:items-center space-x-3 p-3 sm:p-4 border border-purple-200 bg-purple-50 rounded-lg cursor-pointer hover:bg-purple-100 transition-colors min-h-[60px]">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs sm:text-sm font-bold">AI</span>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm sm:text-base font-medium text-gray-900">Let AI choose optimal colors</div>
                            <div className="text-xs sm:text-sm text-gray-600 mt-1">Our AI will select the perfect color palette based on your context and platform</div>
                          </div>
                          <div className="flex-shrink-0 pt-1 sm:pt-0">
                            <input
                              type="radio"
                              name="colorChoice"
                              checked={!brief.colors || brief.colors.length === 0}
                              onChange={() => setBrief(prev => ({ ...prev, colors: [] }))}
                              className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 focus:ring-purple-500"
                            />
                          </div>
                        </label>

                        {/* Custom Colors Option */}
                        <label className="flex items-start sm:items-center space-x-3 p-3 sm:p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors min-h-[60px]">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 rounded-full flex items-center justify-center">
                              <span className="text-gray-600 text-xs sm:text-sm">🎨</span>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm sm:text-base font-medium text-gray-900">Choose specific colors</div>
                            <div className="text-xs sm:text-sm text-gray-600 mt-1">Select your own color palette for brand consistency</div>
                          </div>
                          <div className="flex-shrink-0 pt-1 sm:pt-0">
                            <input
                              type="radio"
                              name="colorChoice"
                              checked={brief.colors && brief.colors.length > 0}
                              onChange={() => setBrief(prev => ({ ...prev, colors: ['#007bff'] }))}
                              className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 focus:ring-purple-500"
                            />
                          </div>
                        </label>

                        {/* Color inputs - only show when custom colors are selected */}
                        {brief.colors && brief.colors.length > 0 && (
                          <div className="flex flex-wrap gap-3 p-3 sm:p-4 border border-gray-200 rounded-lg bg-gray-50">
                            {brief.colors.map((color, index) => (
                              <div key={index} className="flex items-center space-x-2">
                                <input
                                  type="color"
                                  value={color}
                                  onChange={(e) => {
                                    const newColors = [...(brief.colors || [])];
                                    newColors[index] = e.target.value;
                                    setBrief(prev => ({ ...prev, colors: newColors }));
                                  }}
                                  className="w-12 h-12 sm:w-14 sm:h-14 border-2 border-gray-300 rounded-lg cursor-pointer hover:scale-105 transition-transform shadow-sm"
                                />
                                {brief.colors && brief.colors.length > 1 && (
                                  <button
                                    onClick={() => {
                                      const newColors = brief.colors?.filter((_, i) => i !== index) || [];
                                      setBrief(prev => ({ ...prev, colors: newColors }));
                                    }}
                                    className="p-2 text-red-400 hover:text-red-600 min-w-[44px] min-h-[44px] flex items-center justify-center"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            ))}
                            
                            {brief.colors.length < 5 && (
                              <button
                                onClick={() => {
                                  const newColors = [...(brief.colors || []), '#000000'];
                                  setBrief(prev => ({ ...prev, colors: newColors }));
                                }}
                                className="w-12 h-12 sm:w-14 sm:h-14 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:border-gray-400 transition-colors"
                              >
                                <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
                              </button>
                            )}
                          </div>
                        )}

                        {/* Recommended palettes - only show when custom colors are selected */}
                        {brief.colors && brief.colors.length > 0 && recommendedPalettes.length > 0 && (
                          <div>
                            <p className="text-xs text-gray-500 mb-2">Recommended from your assets:</p>
                            <div className="flex flex-wrap gap-2">
                              {recommendedPalettes.map((palette, index) => (
                                <button
                                  key={index}
                                  onClick={() => setBrief(prev => ({ ...prev, colors: palette.colors.slice(0, 5) }))}
                                  className="flex items-center space-x-1 p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                  {palette.colors.slice(0, 3).map((color, colorIndex) => (
                                    <div
                                      key={colorIndex}
                                      className="w-4 h-4 rounded-full border border-gray-200"
                                      style={{ backgroundColor: color }}
                                    />
                                  ))}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end mt-6 sm:mt-8">
                    <button
                      onClick={() => {
                        const validation = validateBrief();
                        if (validation.valid) {
                          navigateToStep('assets');
                        } else {
                          showNotification(validation.errors[0], 'error');
                        }
                      }}
                      className="w-full sm:w-auto px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium flex items-center justify-center space-x-2 min-h-[44px]"
                    >
                      <span>Continue to Assets</span>
                      <ArrowLeft className="h-4 w-4 rotate-180" />
                    </button>
                  </div>
                </motion.div>
              )}

            {/* Step 2: Assets Upload */}
            {activeStep === 'assets' && (
              <motion.div
                key="assets"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6 lg:p-8"
              >
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
                    <FileImage className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 mr-2 flex-shrink-0" />
                    <span>Upload Assets</span>
                  </h2>
                  
                  <div className="space-y-4 sm:space-y-6">
                    {/* Logo Upload/Selection */}
                    <div>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 space-y-2 sm:space-y-0">
                        <label className="text-sm font-medium text-gray-700">
                          Logo (Optional)
                        </label>
                        
                        {/* Toggle buttons for upload/library mode */}
                        <div className="flex bg-gray-100 rounded-lg p-1">
                          <button
                            onClick={() => setLogoSelectionMode('upload')}
                            className={`flex-1 sm:flex-none px-3 py-2 text-xs sm:text-sm font-medium rounded transition-colors min-h-[44px] flex items-center justify-center ${
                              logoSelectionMode === 'upload'
                                ? 'bg-white text-purple-600 shadow-sm'
                                : 'text-gray-600 hover:text-gray-800'
                            }`}
                          >
                            <Upload className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 flex-shrink-0" />
                            <span>Upload New</span>
                          </button>
                          <button
                            onClick={() => setLogoSelectionMode('library')}
                            className={`flex-1 sm:flex-none px-3 py-2 text-xs sm:text-sm font-medium rounded transition-colors min-h-[44px] flex items-center justify-center ${
                              logoSelectionMode === 'library'
                                ? 'bg-white text-purple-600 shadow-sm'
                                : 'text-gray-600 hover:text-gray-800'
                            }`}
                          >
                            <Image className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 flex-shrink-0" />
                            <span>From Library</span>
                          </button>
                        </div>
                      </div>
                      
                      {logoSelectionMode === 'upload' ? (
                        /* Upload Mode */
                        <div
                          onDragOver={(e) => handleDragOver(e, 'logo')}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, 'logo')}
                          className={`
                            relative border-2 border-dashed rounded-lg p-4 sm:p-6 text-center transition-all duration-300 min-h-[120px] sm:min-h-[140px] flex items-center justify-center
                            ${dragOver === 'logo' ? 'border-purple-400 bg-purple-50 scale-105' : 
                              selectedLogo ? 'border-green-300 bg-green-50' : 
                              'border-gray-300 hover:border-purple-300 hover:bg-purple-50/30'}
                          `}
                        >
                          {isUploadingLogo ? (
                            <div className="flex flex-col items-center justify-center space-y-2">
                              <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                              <p className="text-xs text-gray-600">Uploading...</p>
                            </div>
                          ) : selectedLogo ? (
                            <div className="flex items-center space-x-3 w-full">
                              <img
                                src={selectedLogo.asset_url}
                                alt="Uploaded logo"
                                className="w-12 h-12 sm:w-16 sm:h-16 object-contain rounded flex-shrink-0"
                              />
                              <div className="flex-1 text-left min-w-0">
                                <p className="text-sm sm:text-base font-medium text-gray-900 truncate">{selectedLogo.original_filename}</p>
                                <p className="text-xs sm:text-sm text-gray-500">Logo uploaded</p>
                              </div>
                              <button
                                onClick={() => setSelectedLogo(null)}
                                className="p-2 text-gray-400 hover:text-red-500 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0"
                              >
                                <X className="w-4 h-4 sm:w-5 sm:h-5" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center space-y-2">
                              <FileImage className="h-8 w-8 sm:h-10 sm:w-10 text-gray-400" />
                              <div className="space-y-1 text-center">
                                <p className="text-sm sm:text-base text-gray-600">
                                  Drop logo here or click to browse
                                </p>
                                <p className="text-xs sm:text-sm text-gray-500">
                                  PNG, JPG, GIF up to 20MB
                                </p>
                              </div>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleLogoUpload(Array.from(e.target.files || []))}
                                disabled={isUploadingLogo}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                              />
                            </div>
                          )}
                        </div>
                      ) : (
                        /* Library Selection Mode for Logo */
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 sm:p-6 min-h-[120px] sm:min-h-[140px] flex items-center">
                          {selectedLibraryLogoInAssets ? (
                            <div className="flex items-center space-x-3 w-full">
                              <img
                                src={selectedLibraryLogoInAssets.asset_url}
                                alt="Selected library logo"
                                className="w-12 h-12 sm:w-16 sm:h-16 object-contain rounded flex-shrink-0"
                              />
                              <div className="flex-1 text-left min-w-0">
                                <p className="text-sm sm:text-base font-medium text-gray-900 truncate">{selectedLibraryLogoInAssets.original_filename}</p>
                                <p className="text-xs sm:text-sm text-gray-500">Selected from library</p>
                              </div>
                              <button
                                onClick={() => {
                                  setSelectedLibraryLogoInAssets(null);
                                  setSelectedLogo(null);
                                }}
                                className="p-2 text-gray-400 hover:text-red-500 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0"
                              >
                                <X className="w-4 h-4 sm:w-5 sm:h-5" />
                              </button>
                            </div>
                          ) : (
                            <div className="text-center w-full">
                              <Image className="h-8 w-8 sm:h-10 sm:w-10 text-gray-400 mx-auto mb-3" />
                              <button
                                onClick={() => setShowLogoLibraryInAssets(true)}
                                className="text-sm sm:text-base text-purple-600 hover:text-purple-700 font-medium min-h-[44px] px-4 py-2 rounded-lg hover:bg-purple-50 transition-colors"
                              >
                                Browse Logo Library
                              </button>
                              <p className="text-xs sm:text-sm text-gray-500 mt-2">
                                Select from your previously uploaded logos
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Reference Images Upload/Selection */}
                    <div>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 space-y-2 sm:space-y-0">
                        <label className="text-sm font-medium text-gray-700">
                          Reference Images (Optional, up to 5)
                        </label>
                        
                        {/* Toggle buttons for upload/library mode */}
                        <div className="flex bg-gray-100 rounded-lg p-1">
                          <button
                            onClick={() => setReferenceSelectionMode('upload')}
                            className={`flex-1 sm:flex-none px-3 py-2 text-xs sm:text-sm font-medium rounded transition-colors min-h-[44px] flex items-center justify-center ${
                              referenceSelectionMode === 'upload'
                                ? 'bg-white text-purple-600 shadow-sm'
                                : 'text-gray-600 hover:text-gray-800'
                            }`}
                          >
                            <Upload className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 flex-shrink-0" />
                            <span>Upload New</span>
                          </button>
                          <button
                            onClick={() => setReferenceSelectionMode('library')}
                            className={`flex-1 sm:flex-none px-3 py-2 text-xs sm:text-sm font-medium rounded transition-colors min-h-[44px] flex items-center justify-center ${
                              referenceSelectionMode === 'library'
                                ? 'bg-white text-purple-600 shadow-sm'
                                : 'text-gray-600 hover:text-gray-800'
                            }`}
                          >
                            <Image className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 flex-shrink-0" />
                            <span>From Library</span>
                          </button>
                        </div>
                      </div>
                      
                      {referenceSelectionMode === 'upload' ? (
                        /* Upload Mode */
                        <div
                          onDragOver={(e) => handleDragOver(e, 'reference')}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, 'reference')}
                          className={`
                            relative border-2 border-dashed rounded-lg p-4 sm:p-6 text-center transition-all duration-300 min-h-[120px] sm:min-h-[140px]
                            ${dragOver === 'reference' ? 'border-purple-400 bg-purple-50 scale-105' : 
                              selectedReferenceImages.length > 0 ? 'border-green-300 bg-green-50' : 
                              'border-gray-300 hover:border-purple-300 hover:bg-purple-50/30'}
                          `}
                        >
                          {isUploadingReferences ? (
                            <div className="flex flex-col items-center justify-center space-y-2 py-4">
                              <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                              <p className="text-xs sm:text-sm text-gray-600">Uploading images...</p>
                            </div>
                          ) : selectedReferenceImages.length > 0 ? (
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                                {selectedReferenceImages.map((image, index) => (
                                  <div key={image.id} className="relative group">
                                    <img
                                      src={image.asset_url}
                                      alt={`Reference ${index + 1}`}
                                      className="w-full h-16 sm:h-20 object-cover rounded"
                                    />
                                    <button
                                      onClick={() => {
                                        setSelectedReferenceImages(prev => 
                                          prev.filter(img => img.id !== image.id)
                                        );
                                      }}
                                      className="absolute -top-1 -right-1 w-6 h-6 sm:w-7 sm:h-7 bg-red-500 text-white rounded-full flex items-center justify-center opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <X className="w-3 h-3 sm:w-4 sm:h-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                              
                              {selectedReferenceImages.length < 5 && (
                                <div className="border-t pt-3">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={(e) => handleReferenceUpload(Array.from(e.target.files || []))}
                                    disabled={isUploadingReferences}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                                  />
                                  <p className="text-xs sm:text-sm text-gray-600">
                                    Drop more images or click to add ({selectedReferenceImages.length}/5)
                                  </p>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center space-y-2">
                              <FileImage className="h-8 w-8 sm:h-10 sm:w-10 text-gray-400" />
                              <div className="space-y-1 text-center">
                                <p className="text-sm sm:text-base text-gray-600">
                                  Drop reference images here or click to browse
                                </p>
                                <p className="text-xs sm:text-sm text-gray-500">
                                  PNG, JPG, GIF up to 20MB each (max 5 images)
                                </p>
                              </div>
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={(e) => handleReferenceUpload(Array.from(e.target.files || []))}
                                disabled={isUploadingReferences}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                              />
                            </div>
                          )}
                        </div>
                      ) : (
                        /* Library Selection Mode for References */
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 sm:p-6 min-h-[120px] sm:min-h-[140px] flex items-center">
                          {selectedLibraryReferencesInAssets.length > 0 ? (
                            <div className="space-y-3 w-full">
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                                {selectedLibraryReferencesInAssets.map((image, index) => (
                                  <div key={image.id} className="relative group">
                                    <img
                                      src={image.asset_url}
                                      alt={`Library reference ${index + 1}`}
                                      className="w-full h-16 sm:h-20 object-cover rounded"
                                    />
                                    <button
                                      onClick={() => {
                                        setSelectedLibraryReferencesInAssets(prev => 
                                          prev.filter(img => img.id !== image.id)
                                        );
                                        setSelectedReferenceImages(prev => 
                                          prev.filter(img => img.id !== image.id)
                                        );
                                      }}
                                      className="absolute -top-1 -right-1 w-6 h-6 sm:w-7 sm:h-7 bg-red-500 text-white rounded-full flex items-center justify-center opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <X className="w-3 h-3 sm:w-4 sm:h-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                              
                              {selectedLibraryReferencesInAssets.length < 5 && (
                                <div className="border-t pt-3 text-center">
                                  <button
                                    onClick={() => setShowReferenceLibraryInAssets(true)}
                                    className="text-xs sm:text-sm text-purple-600 hover:text-purple-700 font-medium min-h-[44px] px-4 py-2 rounded-lg hover:bg-purple-50 transition-colors"
                                  >
                                    Add more from library ({selectedLibraryReferencesInAssets.length}/5)
                                  </button>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-center w-full">
                              <FileImage className="h-8 w-8 sm:h-10 sm:w-10 text-gray-400 mx-auto mb-3" />
                              <button
                                onClick={() => setShowReferenceLibraryInAssets(true)}
                                className="text-sm sm:text-base text-purple-600 hover:text-purple-700 font-medium min-h-[44px] px-4 py-2 rounded-lg hover:bg-purple-50 transition-colors"
                              >
                                Browse Reference Library
                              </button>
                              <p className="text-xs sm:text-sm text-gray-500 mt-2">
                                Select from your previously uploaded references
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-between mt-6 sm:mt-8 space-y-3 sm:space-y-0 sm:space-x-4">
                    <button
                      onClick={() => navigateToStep('brief')}
                      className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium flex items-center justify-center space-x-2 min-h-[44px] order-2 sm:order-1"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      <span>Back to Brief</span>
                    </button>
                    <button
                      onClick={handleGenerate}
                      disabled={creditBalance < 1}
                      className="w-full sm:w-auto px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 min-h-[44px] order-1 sm:order-2"
                    >
                      <Sparkles className="h-4 w-4" />
                      <span>Generate Ad (1 credit)</span>
                    </button>
                  </div>
                </motion.div>
              )}

            {/* Step 3: Generating */}
            {activeStep === 'generate' && (
              <motion.div
                key="generate"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-xl shadow-lg border border-gray-200 p-8"
              >
                <div className="text-center py-12">
                  {/* Animated loader */}
                  <div className="relative mb-8">
                    <div className="w-32 h-32 rounded-full bg-purple-100 flex items-center justify-center mx-auto">
                      <Sparkles className="w-16 h-16 text-purple-600 animate-pulse" />
                    </div>
                    <div className="absolute inset-0 w-32 h-32 rounded-full border-4 border-purple-600 border-t-transparent animate-spin mx-auto" />
                  </div>
                  
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                    Generating Your Ad...
                  </h2>
                  
                  {/* Rotating message */}
                  <motion.p
                    key={loadingMessageIndex}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="text-lg text-gray-700 mb-8"
                  >
                    {loadingMessages[loadingMessageIndex]}
                  </motion.p>
                  
                  <p className="text-sm text-gray-500 mb-8">
                    This usually takes 1-5 minutes
                  </p>
                  
                  <div className="max-w-md mx-auto bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-700 space-y-2">
                      <div>
                        <span className="font-medium">Context:</span>
                        <p className="text-xs mt-1 text-gray-600">
                          {brief.context}
                        </p>
                      </div>
                      <div className="flex justify-between">
                        <span>Platform:</span>
                        <span className="font-medium">{brief.platform}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Style:</span>
                        <span className="font-medium">{brief.style}</span>
                      </div>
                      {brief.headline && (
                        <div className="flex justify-between">
                          <span>Headline:</span>
                          <span className="font-medium">{brief.headline}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
              )}

            {/* Step 4: Result */}
            {activeStep === 'result' && generationResult && (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 overflow-visible relative"
                style={{ pointerEvents: 'auto' }}
              >
                  <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                    <Eye className="h-5 w-5 text-purple-600 mr-2" />
                    Generated Ad
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      (Generation #{generationResult.generationCount})
                    </span>
                  </h2>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Generated Image */}
                    <div>
                      <SecureImage
                        src={generationResult.generatedImageUrl}
                        alt="Generated ad"
                        className="w-full rounded-lg shadow-sm"
                      />
                    </div>
                    
                    {/* Actions and Details */}
                    <div className="space-y-6">
                      {/* Action Buttons */}
                      <div className="grid grid-cols-1 gap-3">
                        <button
                          onClick={handleDownload}
                          className="flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                        >
                          <Download className="h-4 w-4" />
                          <span>Download</span>
                        </button>
                        
                        <button
                          onClick={handleAnalyze}
                          className="flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                        >
                          <Target className="h-4 w-4" />
                          <span>Analyze for Compliance</span>
                        </button>
                        
                        
                        <button
                          onClick={() => {
                            // Set the current generation result for regeneration
                            setRegeneratingGeneration(generationResult);
                            lockBodyScroll();
                            setShowRegenerateModal(true);
                          }}
                          disabled={isGenerating || creditBalance < 1}
                          className="flex items-center justify-center space-x-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <RotateCcw className="h-4 w-4" />
                          <span>Regenerate (1 credit)</span>
                        </button>
                      </div>
                      
                      {/* Generation Details */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-3">Generation Details</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Credits Used:</span>
                            <span className="font-medium">{generationResult.creditsUsed}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Credits Remaining:</span>
                            <span className="font-medium">{generationResult.creditsRemaining}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Generation Count:</span>
                            <span className="font-medium">#{generationResult.generationCount}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  
                  <div className="flex justify-between mt-8">
                    <button
                      onClick={createNewAd}
                      className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium flex items-center space-x-2 shadow-sm"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Create New Ad</span>
                    </button>
                  </div>
                </motion.div>
              )}
          </AnimatePresence>
        </div>
        
        {/* Ad Generation History Section */}
        {(generationHistory.length > 0 || isHistoryLoading) && (
          <div id="generation-history" className="max-w-7xl mx-auto mt-8 sm:mt-12">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6 lg:p-8">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div className="flex items-center space-x-3">
                  <History className="h-6 w-6 text-purple-600" />
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Generation History</h2>
                  <span className="text-sm text-gray-500">
                    {isHistoryLoading ? (
                      <span className="inline-flex items-center space-x-1">
                        <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                        <span>Loading...</span>
                      </span>
                    ) : (
                      filterGenerations(generationHistory).length === generationHistory.length 
                        ? `(${generationHistory.length} of ${totalHistoryCount} total)`
                        : `(${filterGenerations(generationHistory).length} filtered of ${generationHistory.length} loaded)`
                    )}
                  </span>
                </div>
                
                {/* Active filter count badge */}
                {getActiveFilterCount() > 0 && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-purple-600 font-medium">
                      {getActiveFilterCount()} filter{getActiveFilterCount() > 1 ? 's' : ''} active
                    </span>
                    <button
                      onClick={clearAllFilters}
                      className="text-sm text-gray-500 hover:text-gray-700 underline min-h-[44px] px-2 py-1 touch-manipulation"
                    >
                      Clear all
                    </button>
                  </div>
                )}
              </div>
              
              {/* Filter Bar */}
              <div className="border-b border-gray-200 pb-4 mb-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap items-start lg:items-center gap-3">
                  {/* Search */}
                  <div className="relative sm:col-span-2 lg:flex-1 lg:min-w-[200px] lg:max-w-[300px]">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder={isHistoryLoading ? "Loading..." : "Search generations..."}
                      value={historySearchTerm}
                      onChange={(e) => setHistorySearchTerm(e.target.value)}
                      disabled={isHistoryLoading}
                      className="w-full pl-10 pr-4 py-2 min-h-[44px] text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                    />
                  </div>
                  
                  {/* Platform Filter */}
                  <select
                    value={historyFilters.platform}
                    onChange={(e) => setHistoryFilters(prev => ({ ...prev, platform: e.target.value }))}
                    className="px-3 py-2 min-h-[44px] text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white touch-manipulation"
                  >
                    <option value="all">All Platforms</option>
                    {platformOptions.map(platform => (
                      <option key={platform} value={platform}>{platform}</option>
                    ))}
                  </select>
                  
                  {/* Style Filter */}
                  <select
                    value={historyFilters.style}
                    onChange={(e) => setHistoryFilters(prev => ({ ...prev, style: e.target.value }))}
                    className="px-3 py-2 min-h-[44px] text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white touch-manipulation"
                  >
                    <option value="all">All Styles</option>
                    {styleOptions.map(style => (
                      <option key={style} value={style}>{style}</option>
                    ))}
                  </select>
                  
                  {/* Size Filter */}
                  <select
                    value={historyFilters.size}
                    onChange={(e) => setHistoryFilters(prev => ({ ...prev, size: e.target.value }))}
                    className="px-3 py-2 min-h-[44px] text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white touch-manipulation"
                  >
                    <option value="all">All Sizes</option>
                    {sizeOptions.map(size => (
                      <option key={size.value} value={size.value}>{size.label}</option>
                    ))}
                  </select>
                  
                  {/* Date Range Filter */}
                  <select
                    value={historyFilters.dateRange}
                    onChange={(e) => setHistoryFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                    className="px-3 py-2 min-h-[44px] text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white touch-manipulation"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">Last 7 Days</option>
                    <option value="month">Last 30 Days</option>
                  </select>
                </div>
                
                {/* Active Filters Display */}
                {(getActiveFilterCount() > 0 || historySearchTerm) && (
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    {historySearchTerm && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                        <Search className="h-3 w-3" />
                        "{historySearchTerm}"
                        <button
                          onClick={() => setHistorySearchTerm('')}
                          className="ml-1 p-2 min-w-[36px] min-h-[36px] flex items-center justify-center hover:text-purple-900 touch-manipulation rounded-lg hover:bg-purple-50 transition-colors"
                        >
                          <XIcon className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {historyFilters.platform !== 'all' && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                        Platform: {historyFilters.platform}
                        <button
                          onClick={() => setHistoryFilters(prev => ({ ...prev, platform: 'all' }))}
                          className="ml-1 p-2 min-w-[36px] min-h-[36px] flex items-center justify-center hover:text-blue-900 touch-manipulation rounded-lg hover:bg-blue-50 transition-colors"
                        >
                          <XIcon className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {historyFilters.style !== 'all' && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                        Style: {historyFilters.style}
                        <button
                          onClick={() => setHistoryFilters(prev => ({ ...prev, style: 'all' }))}
                          className="ml-1 p-2 min-w-[36px] min-h-[36px] flex items-center justify-center hover:text-green-900 touch-manipulation rounded-lg hover:bg-green-50 transition-colors"
                        >
                          <XIcon className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {historyFilters.size !== 'all' && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm">
                        Size: {sizeOptions.find(s => s.value === historyFilters.size)?.label}
                        <button
                          onClick={() => setHistoryFilters(prev => ({ ...prev, size: 'all' }))}
                          className="ml-1 p-2 min-w-[36px] min-h-[36px] flex items-center justify-center hover:text-yellow-900 touch-manipulation rounded-lg hover:bg-yellow-50 transition-colors"
                        >
                          <XIcon className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {historyFilters.dateRange !== 'all' && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">
                        <Calendar className="h-3 w-3" />
                        {historyFilters.dateRange === 'today' && 'Today'}
                        {historyFilters.dateRange === 'week' && 'Last 7 Days'}
                        {historyFilters.dateRange === 'month' && 'Last 30 Days'}
                        <button
                          onClick={() => setHistoryFilters(prev => ({ ...prev, dateRange: 'all' }))}
                          className="ml-1 p-2 min-w-[36px] min-h-[36px] flex items-center justify-center hover:text-orange-900 touch-manipulation rounded-lg hover:bg-orange-50 transition-colors"
                        >
                          <XIcon className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                  </div>
                )}
              </div>
              
              {/* History Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                {isHistoryLoading ? (
                  // Loading skeleton cards
                  [...Array(6)].map((_, index) => (
                    <div key={`skeleton-${index}`} className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
                      {/* Skeleton thumbnail */}
                      <div className="aspect-square bg-gray-200 animate-pulse" />
                      
                      {/* Skeleton content */}
                      <div className="p-4 space-y-3">
                        <div className="h-4 bg-gray-200 rounded animate-pulse" />
                        <div className="flex space-x-2">
                          <div className="h-3 bg-gray-200 rounded animate-pulse flex-1" />
                          <div className="h-3 bg-gray-200 rounded animate-pulse w-16" />
                        </div>
                        <div className="h-3 bg-gray-200 rounded animate-pulse w-24" />
                      </div>
                    </div>
                  ))
                ) : (
                  filterGenerations(generationHistory)
                    .map((generation) => (
                    <motion.div
                      key={generation.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      whileHover={{ y: -4 }}
                      className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200 hover:shadow-lg transition-all duration-300 group cursor-pointer"
                      onClick={() => {
                        setSelectedCreative(generation);
                        lockBodyScroll();
                        setShowCreativeModal(true);
                      }}
                    >
                      {/* Thumbnail */}
                      {generation.generated_image_url && (
                        <div className="relative aspect-square overflow-hidden bg-gray-100">
                          <SecureImage
                            src={generation.thumbnail_url || generation.generated_image_url}
                            alt="Generated ad"
                            className="w-full h-full object-cover"
                            fallbackSrc={generation.generated_image_url}
                          />
                          
                          {/* Thumbnail indicator */}
                          {generation.thumbnail_url && (
                            <div className="absolute bottom-2 left-2 bg-green-600 text-white text-xs px-1.5 py-0.5 rounded opacity-75">
                              Fast
                            </div>
                          )}
                          
                          {/* No thumbnail indicator */}
                          {!generation.thumbnail_url && (
                            <div className="absolute bottom-2 left-2 bg-orange-600 text-white text-xs px-1.5 py-0.5 rounded opacity-75">
                              Full
                            </div>
                          )}
                          
                          {/* Hover Actions */}
                          <div className="absolute inset-0 bg-black bg-opacity-60 opacity-0 sm:opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center space-x-2">
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (generation.generated_image_url) {
                                  const success = await downloadImage(
                                    generation.generated_image_url,
                                    `rulevia-ad-${generation.id}.png`
                                  );
                                  if (success) {
                                    showNotification('Ad downloaded successfully!', 'success');
                                  } else {
                                    showNotification('Download failed. Please try again.', 'error');
                                  }
                                }
                              }}
                              className="p-2 bg-white rounded-full text-gray-800 hover:bg-gray-100 transition-colors"
                              title="Download"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                            
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                // Ensure all fields have proper default values to prevent controlled/uncontrolled input errors
                                setBrief({
                                  context: generation.brief_data.context || '',
                                  headline: generation.brief_data.headline || '',
                                  cta: generation.brief_data.cta || '',
                                  stickerText: generation.brief_data.stickerText || '',
                                  style: generation.brief_data.style || 'Modern',
                                  colors: generation.brief_data.colors || [],
                                  platform: generation.brief_data.platform || 'Facebook',
                                  size: generation.brief_data.size || 'auto'
                                });
                                navigateToStep('brief');
                              }}
                              className="p-2 bg-white rounded-full text-gray-800 hover:bg-gray-100 transition-colors"
                              title="Reuse Brief"
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                            
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCreative(generation);
                                lockBodyScroll();
                                setShowCreativeModal(true);
                              }}
                              className="p-2 bg-white rounded-full text-gray-800 hover:bg-gray-100 transition-colors"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setRegeneratingGeneration(generation);
                                lockBodyScroll();
                                setShowRegenerateModal(true);
                              }}
                              disabled={creditBalance < 1}
                              className="p-2 bg-white rounded-full text-gray-800 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Regenerate (1 credit)"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </button>
                          </div>
                          
                          {/* Generation Count Badge */}
                          <div className="absolute top-2 right-2 bg-purple-600 text-white text-xs px-2 py-1 rounded-full">
                            #{generation.generation_count}
                          </div>
                        </div>
                      )}
                      
                      {/* Details */}
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-purple-600">
                            {generation.brief_data.platform}
                          </span>
                          <span className="text-xs text-gray-500">
                            {generation.brief_data.style}
                          </span>
                        </div>
                        
                        {generation.brief_data.headline && (
                          <h3 className="font-medium text-gray-900 mb-1 line-clamp-1">
                            {generation.brief_data.headline}
                          </h3>
                        )}
                        
                        <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                          {generation.brief_data.context}
                        </p>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">
                            {new Date(generation.created_at).toLocaleDateString()}
                          </span>
                          <button className="text-gray-400 hover:text-gray-600">
                            <MoreVertical className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
              
              {/* Load More Button */}
              {generationHistory.length >= historyLimit && totalHistoryCount > historyLimit && (
                <div className="text-center mt-8">
                  <button
                    onClick={loadMoreHistory}
                    disabled={isLoadingMoreHistory}
                    className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium inline-flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoadingMoreHistory ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Loading...</span>
                      </>
                    ) : (
                      <>
                        <History className="h-4 w-4" />
                        <span>Load More ({totalHistoryCount - historyLimit} more)</span>
                      </>
                    )}
                  </button>
                </div>
              )}
              
              {/* Empty State */}
              {filterGenerations(generationHistory).length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 mb-2">
                    <Search className="h-12 w-12 mx-auto" />
                  </div>
                  <p className="text-gray-600">No generations found matching your search.</p>
                </div>
              ) : (
                !showMoreHistory && filterGenerations(generationHistory).length > 8 && (
                  <div className="text-center mt-8">
                    <button
                      onClick={() => setShowMoreHistory(true)}
                      className="px-6 py-3 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors font-medium"
                    >
                      Show More ({filterGenerations(generationHistory).length - 8} more)
                    </button>
                  </div>
                )
              )}
            </div>
          </div>
        )}

        {/* Asset Library Section */}
        <div id="asset-library" className="max-w-7xl mx-auto mt-12">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
            {/* Asset Library Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <FileImage className="h-6 w-6 text-purple-600" />
                <h2 className="text-2xl font-bold text-gray-900">Asset Library</h2>
                <span className="text-sm text-gray-500">
                  ({assetLibrary.logos.length} logos, {assetLibrary.referenceImages.length} references)
                </span>
              </div>
              <button
                onClick={() => setShowAssetLibrary(!showAssetLibrary)}
                className="flex items-center space-x-2 px-4 py-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              >
                <span>{showAssetLibrary ? 'Hide' : 'Show'} Assets</span>
                {showAssetLibrary ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>

            {/* Asset Library Content */}
            {showAssetLibrary && (
              <div className="space-y-6">
                {/* Search and Filter Controls */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search assets by filename..."
                        value={assetSearchTerm}
                        onChange={(e) => {
                          setAssetSearchTerm(e.target.value);
                          handleSearchAssets(e.target.value);
                        }}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={selectedAssetType}
                      onChange={(e) => {
                        const newType = e.target.value as 'all' | 'logo' | 'reference';
                        setSelectedAssetType(newType);
                        handleSearchAssets(assetSearchTerm);
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="all">All Assets</option>
                      <option value="logo">Logos Only</option>
                      <option value="reference">References Only</option>
                    </select>
                    <button
                      onClick={() => {
                        setAssetSearchTerm('');
                        setSelectedAssetType('all');
                        loadAssetLibrary();
                      }}
                      className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                {/* Logos Section */}
                {(selectedAssetType === 'all' || selectedAssetType === 'logo') && assetLibrary.logos.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <FileImage className="w-5 h-5 mr-2" />
                      Logos ({assetLibrary.logos.length}/10)
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {assetLibrary.logos.map((logo) => (
                        <div key={logo.id} className="group relative">
                          <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-purple-300 transition-colors">
                            <img
                              src={logo.asset_url}
                              alt={logo.original_filename}
                              className="w-full h-full object-contain p-2"
                            />
                          </div>
                          <div className="mt-2">
                            <p className="text-xs text-gray-600 truncate" title={logo.original_filename}>
                              {logo.original_filename}
                            </p>
                            <p className="text-xs text-gray-400">
                              {(logo.file_size / 1024).toFixed(1)} KB • Used {logo.usage_count}x
                            </p>
                          </div>
                          {/* Delete Button */}
                          <button
                            onClick={() => handleDeleteAsset(logo.id, 'logo')}
                            className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 touch-manipulation"
                            title="Delete logo"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reference Images Section */}
                {(selectedAssetType === 'all' || selectedAssetType === 'reference') && assetLibrary.referenceImages.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <Image className="w-5 h-5 mr-2" />
                      Reference Images ({assetLibrary.referenceImages.length}/50)
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {assetLibrary.referenceImages.map((reference) => (
                        <div key={reference.id} className="group relative">
                          <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-purple-300 transition-colors">
                            <img
                              src={reference.asset_url}
                              alt={reference.original_filename}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="mt-2">
                            <p className="text-xs text-gray-600 truncate" title={reference.original_filename}>
                              {reference.original_filename}
                            </p>
                            <p className="text-xs text-gray-400">
                              {(reference.file_size / 1024).toFixed(1)} KB • Used {reference.usage_count}x
                            </p>
                          </div>
                          {/* Delete Button */}
                          <button
                            onClick={() => handleDeleteAsset(reference.id, 'reference')}
                            className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 touch-manipulation"
                            title="Delete reference image"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {assetLibrary.logos.length === 0 && assetLibrary.referenceImages.length === 0 && (
                  <div className="text-center py-12">
                    <FileImage className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No assets found</h3>
                    <p className="text-gray-500">
                      {assetSearchTerm ? 'No assets match your search criteria.' : 'Upload logos and reference images to get started.'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Empty State for History */}
        {generationHistory.length === 0 && (
          <div className="max-w-4xl mx-auto mt-12">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-12 text-center">
              <div className="text-gray-400 mb-4">
                <Sparkles className="h-16 w-16 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No ads generated yet</h3>
              <p className="text-gray-600 mb-6">Start creating amazing ads with AI! Your generation history will appear here.</p>
              <button
                onClick={() => navigateToStep('brief')}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium inline-flex items-center space-x-2"
              >
                <Sparkles className="h-4 w-4" />
                <span>Create Your First Ad</span>
              </button>
            </div>
          </div>
        )}
      
      {/* Asset Library Modals for Assets Step */}
      <AnimatePresence>
        {/* Logo Library Modal for Assets Step */}
        {showLogoLibraryInAssets && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowLogoLibraryInAssets(false);
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
            >
              {/* Library Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Image className="w-5 h-5 text-purple-600 mr-2" />
                  Select Logo from Library
                </h3>
                <button
                  onClick={() => setShowLogoLibraryInAssets(false)}
                  className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors touch-manipulation"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              {/* Library Content */}
              <div className="p-4 overflow-y-auto max-h-[calc(100vh-10rem)] min-h-0">
                {assetLibrary.logos.length > 0 ? (
                  <div className="grid grid-cols-3 gap-4">
                    {assetLibrary.logos.map((logo) => (
                      <button
                        key={logo.id}
                        onClick={() => handleLogoLibrarySelectionInAssets(logo)}
                        className={`relative group border-2 rounded-lg p-4 transition-all hover:border-purple-400 hover:shadow-md ${
                          selectedLibraryLogoInAssets?.id === logo.id ? 'border-purple-600 bg-purple-50' : 'border-gray-200'
                        }`}
                      >
                        <img
                          src={logo.asset_url}
                          alt={logo.original_filename}
                          className="w-full h-20 object-contain mb-2"
                        />
                        <p className="text-xs text-gray-600 truncate">{logo.original_filename}</p>
                        <div className="absolute inset-0 bg-purple-600 bg-opacity-0 group-hover:bg-opacity-5 rounded-lg transition-all" />
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Image className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No logos in your library yet</p>
                    <p className="text-sm text-gray-400 mt-1">Upload logos and they'll appear here</p>
                  </div>
                )}
              </div>
              
              {/* Library Modal Footer */}
              <div className="flex justify-end p-4 border-t border-gray-200">
                <button
                  onClick={() => setShowLogoLibraryInAssets(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Reference Library Modal for Assets Step */}
        {showReferenceLibraryInAssets && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowReferenceLibraryInAssets(false);
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden"
            >
              {/* Library Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <FileImage className="w-5 h-5 text-purple-600 mr-2" />
                  Select Reference Images from Library
                </h3>
                <button
                  onClick={() => setShowReferenceLibraryInAssets(false)}
                  className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors touch-manipulation"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              {/* Library Content */}
              <div className="p-4 overflow-y-auto max-h-[calc(100vh-10rem)] min-h-0">
                {assetLibrary.referenceImages.length > 0 ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-4 gap-4">
                      {assetLibrary.referenceImages.map((image) => {
                        const isSelected = selectedLibraryReferencesInAssets.some(ref => ref.id === image.id);
                        const canAdd = selectedLibraryReferencesInAssets.length < 5;
                        
                        return (
                          <button
                            key={image.id}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedLibraryReferencesInAssets(prev => 
                                  prev.filter(ref => ref.id !== image.id)
                                );
                              } else if (canAdd) {
                                setSelectedLibraryReferencesInAssets(prev => [...prev, image]);
                              }
                            }}
                            disabled={!isSelected && !canAdd}
                            className={`relative group border-2 rounded-lg overflow-hidden transition-all ${
                              isSelected 
                                ? 'border-purple-600 ring-2 ring-purple-400' 
                                : canAdd
                                  ? 'border-gray-200 hover:border-purple-400 hover:shadow-md'
                                  : 'border-gray-200 opacity-50 cursor-not-allowed'
                            }`}
                          >
                            <img
                              src={image.asset_url}
                              alt={image.original_filename}
                              className="w-full h-24 object-cover"
                            />
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                              <p className="text-xs text-white truncate">{image.original_filename}</p>
                            </div>
                            {isSelected && (
                              <div className="absolute top-1 right-1 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center">
                                <CheckCircle className="w-4 h-4" />
                              </div>
                            )}
                            <div className="absolute inset-0 bg-purple-600 bg-opacity-0 group-hover:bg-opacity-5 transition-all" />
                          </button>
                        );
                      })}
                    </div>
                    
                    {/* Selection Info */}
                    <div className="text-center text-sm text-gray-600 border-t pt-4">
                      {selectedLibraryReferencesInAssets.length > 0 ? (
                        <p>{selectedLibraryReferencesInAssets.length}/5 images selected</p>
                      ) : (
                        <p>Select up to 5 reference images</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileImage className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No reference images in your library yet</p>
                    <p className="text-sm text-gray-400 mt-1">Upload reference images and they'll appear here</p>
                  </div>
                )}
              </div>
              
              {/* Library Modal Footer */}
              <div className="flex justify-between items-center p-4 border-t border-gray-200">
                <span className="text-sm text-gray-600">
                  {selectedLibraryReferencesInAssets.length} of 5 selected
                </span>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowReferenceLibraryInAssets(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      handleReferenceLibrarySelectionInAssets(selectedLibraryReferencesInAssets);
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Use Selected ({selectedLibraryReferencesInAssets.length})
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Render Modals */}
      <CreativeViewModal />
      <AnimatePresence mode="wait" onExitComplete={() => {
        // Ensure clean up after modal exit animation
        setTimeout(() => {
          document.documentElement.style.overflow = '';
          document.body.style.overflow = '';
          document.body.style.pointerEvents = '';
        }, 10);
      }}>
        {showRegenerateModal && regeneratingGeneration && (
          <RegenerateModal 
            key={`regenerate-${getGenerationFields(regeneratingGeneration).id}`}
            error={regenerationError}
            onClearError={() => setRegenerationError(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}